import { useState, useEffect, useRef } from "react";
import { Link, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts";
import {
  PawPrint,
  LayoutDashboard,
  Dog,
  Users,
  Heart,
  HandHeart,
  FileText,
  Settings,
  LogOut,
  Plus,
  Search,
  Filter,
  Edit,
  Trash2,
  Eye,
  ChevronRight,
  TrendingUp,
  Calendar,
  AlertCircle,
  CheckCircle,
  Clock,
  Camera,
  ExternalLink,
  Upload,
  Sparkles,
  MessageCircle,
  Send,
  Image as ImageIcon,
  Shield,
  AlertTriangle,
  ClipboardList,
  MapPin,
  Bell,
  Syringe,
  Weight,
  Stethoscope,
  X,
  Video,
  Link2,
  RefreshCw,
  Play,
  Pause,
  Menu,
  Activity,
  TrendingDown,
  Minus,
} from "lucide-react";
import ApplicationsPanel from "@/components/dashboard/ApplicationsPanel";
import NotificationsPanel from "@/components/dashboard/NotificationsPanel";
import DogUpdatesPanel from "@/components/dashboard/DogUpdatesPanel";
import ContentManagement from "@/components/dashboard/ContentManagement";
import FonzipPanel from "@/components/dashboard/FonzipPanel";
import GuardianIntentsPanel from "@/components/dashboard/GuardianIntentsPanel";
import DonationsManagement from "@/components/dashboard/DonationsManagement";
import AdoptionEventsPanel from "@/components/dashboard/AdoptionEventsPanel";
import AdoptionReturnsPanel from "@/components/dashboard/AdoptionReturnsPanel";

export default function DashboardPage() {
  const { user, logout, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingCount, setPendingCount] = useState(0);
  const [channelEnabled, setChannelEnabled] = useState(true);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      navigate("/");
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/admin/notification-channel-status`);
        if (cancelled) return;
        setPendingCount(res.data.pending_queue_count || 0);
        setChannelEnabled(!!res.data.email_channel_enabled);
      } catch (e) { /* silent */ }
    };
    poll();
    const t = setInterval(poll, 60000); // refresh every minute
    // Listen for explicit refresh events from child panels (e.g. after queue delete).
    const onRefresh = () => poll();
    window.addEventListener("kahu:notif-queue-changed", onRefresh);
    return () => {
      cancelled = true;
      clearInterval(t);
      window.removeEventListener("kahu:notif-queue-changed", onRefresh);
    };
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const menuGroups = [
    {
      key: "insights",
      label: "İçgörüler",
      items: [
        { icon: LayoutDashboard, label: "Genel Bakış", path: "/dashboard" },
        { icon: Shield, label: "Şeffaflık", path: "/dashboard/transparency", roles: ["admin"] },
        { icon: Activity, label: "Pilot Raporu", path: "/dashboard/pilot", roles: ["admin"] },
      ],
    },
    {
      key: "operations",
      label: "Operasyon",
      items: [
        { icon: Dog, label: "Canlar", path: "/dashboard/dogs" },
        { icon: FileText, label: "Başvurular", path: "/dashboard/applications" },
        { icon: Calendar, label: "Etkinlikler", path: "/dashboard/events" },
        { icon: RefreshCw, label: "Sahiplendirme İadeleri", path: "/dashboard/returns" },
      ],
    },
    {
      key: "finance",
      label: "Finans",
      items: [
        { icon: Heart, label: "Koruyucu Aile / Düzenli Bağış", path: "/dashboard/guardians" },
        { icon: HandHeart, label: "Bağışlar", path: "/dashboard/donations" },
      ],
    },
    {
      key: "system",
      label: "Sistem",
      items: [
        { icon: Users, label: "Kullanıcılar", path: "/dashboard/users", roles: ["admin"] },
        { icon: Bell, label: "Bildirimler", path: "/dashboard/notifications", roles: ["admin"] },
        { icon: Settings, label: "İçerik Yönetimi", path: "/dashboard/content", roles: ["admin"] },
      ],
    },
    {
      key: "future",
      label: "🔮 Yakında",
      items: [
        { icon: TrendingUp, label: "Barınak Panorama", path: "/dashboard/municipality", roles: ["admin"] },
      ],
    },
  ];

  const filteredGroups = menuGroups
    .map(g => ({
      ...g,
      items: g.items.filter(item => !item.roles || item.roles.includes(user.role)),
    }))
    .filter(g => g.items.length > 0);

  const isActive = (path) => {
    if (path === "/dashboard") return location.pathname === "/dashboard";
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className="w-64 bg-card border-r border-border/50 fixed h-full hidden lg:flex lg:flex-col">
        <div className="p-4 flex-shrink-0">
          <Link to="/" className="flex items-center gap-2">
            <PawPrint className="h-8 w-8 text-primary" strokeWidth={1.5} />
            <span className="font-heading text-2xl font-semibold">KAHU</span>
          </Link>
        </div>

        <nav className="px-4 space-y-3 flex-1 overflow-y-auto pb-3">
          {filteredGroups.map((group) => (
            <div key={group.key}>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 px-4 mb-1">
                {group.label}
              </p>
              <div className="space-y-0.5">
                {group.items.map((item) => {
                  const isNotifications = item.path === "/dashboard/notifications";
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`sidebar-item flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all ${
                        isActive(item.path) ? "active" : "text-muted-foreground hover:text-foreground"
                      }`}
                      data-testid={`menu-${item.path.split('/').pop()}`}
                    >
                      <item.icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                      <span className="flex-1">{item.label}</span>
                      {isNotifications && pendingCount > 0 && (
                        <span
                          className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                            channelEnabled ? "bg-primary/20 text-primary" : "bg-amber-200 text-amber-800"
                          }`}
                          title={channelEnabled ? `${pendingCount} bekleyen bildirim` : `${pendingCount} kuyrukta bekleyen (kanal kapalı)`}
                          data-testid="pending-notifications-badge"
                        >
                          {pendingCount}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="flex-shrink-0 p-3 border-t border-border/50 bg-card">
          <div className="flex items-center gap-3 px-2 py-1">
            <img
              src={user.picture || `https://ui-avatars.com/api/?name=${user.name}&background=random`}
              alt={user.name}
              className="w-9 h-9 rounded-full"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user.name}</p>
              <p className="text-xs text-muted-foreground capitalize">{user.role}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="w-full mt-1.5 justify-start text-muted-foreground"
            onClick={logout}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Çıkış Yap
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 min-w-0 lg:ml-64">
        {/* Mobile Header */}
        <header className="lg:hidden glass-nav sticky top-0 z-50 border-b border-border/50 px-4 py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <PawPrint className="h-6 w-6 text-primary" strokeWidth={1.5} />
              <span className="font-heading text-xl font-semibold">KAHU</span>
            </Link>
            <div className="flex items-center gap-1">
              <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-11 w-11"
                    aria-label="Menüyü aç"
                    data-testid="dashboard-mobile-menu-trigger"
                  >
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[280px] p-0 flex flex-col">
                  <SheetTitle className="sr-only">Yönetim Menüsü</SheetTitle>
                  <div className="p-5 border-b border-border/50 flex items-center gap-2">
                    <PawPrint className="h-7 w-7 text-primary" strokeWidth={1.5} />
                    <span className="font-heading text-xl font-semibold">KAHU</span>
                  </div>
                  <nav className="flex-1 overflow-y-auto py-2">
                    {filteredGroups.map((group) => (
                      <div key={group.key} className="mb-2">
                        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70 px-5 pt-3 pb-1.5">
                          {group.label}
                        </p>
                        {group.items.map((item) => {
                          const isNotifications = item.path === "/dashboard/notifications";
                          return (
                            <Link
                              key={item.path}
                              to={item.path}
                              onClick={() => setMobileNavOpen(false)}
                              className={`flex items-center gap-3 px-5 py-3.5 transition-colors ${
                                isActive(item.path)
                                  ? "bg-primary/10 text-primary border-l-4 border-primary"
                                  : "text-foreground hover:bg-muted"
                              }`}
                              data-testid={`mobile-menu-${item.path.split('/').pop()}`}
                            >
                              <item.icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                              <span className="flex-1 text-sm">{item.label}</span>
                              {isNotifications && pendingCount > 0 && (
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                                  channelEnabled ? "bg-primary/20 text-primary" : "bg-amber-200 text-amber-800"
                                }`}>
                                  {pendingCount}
                                </span>
                              )}
                            </Link>
                          );
                        })}
                      </div>
                    ))}
                  </nav>
                  <div className="p-4 border-t border-border/50">
                    <div className="flex items-center gap-3 px-2 py-2 mb-2">
                      <img
                        src={user.picture || `https://ui-avatars.com/api/?name=${user.name}&background=random`}
                        alt={user.name}
                        className="w-9 h-9 rounded-full"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{user.name}</p>
                        <p className="text-xs text-muted-foreground capitalize">{user.role}</p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => {
                        setMobileNavOpen(false);
                        logout();
                      }}
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Çıkış Yap
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </header>

        <div className="p-6 md:p-8">
          <Routes>
            <Route index element={<DashboardOverview user={user} />} />
            <Route path="dogs" element={<DogsManagement user={user} />} />
            <Route path="transparency" element={<TransparencyManagement />} />
            <Route path="pilot" element={<PilotDashboard />} />
            <Route path="applications" element={<ApplicationsPanel />} />
            <Route path="guardians" element={<GuardianHub />} />
            <Route path="donations" element={<DonationsManagement />} />
            <Route path="events" element={<AdoptionEventsPanel />} />
            <Route path="returns" element={<AdoptionReturnsPanel />} />
            <Route path="users" element={<UsersManagement />} />
            <Route path="notifications" element={<NotificationsPanel />} />
            <Route path="content" element={<ContentManagement />} />
            <Route path="fonzip" element={<GuardianHub defaultTab="fonzip" />} />
            <Route path="municipality" element={<MunicipalityPanel />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

// Dashboard Overview Component
function DashboardOverview({ user }) {
  const [stats, setStats] = useState(null);
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [recentEvents, setRecentEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, monthlyRes, eventsRes] = await Promise.all([
        axios.get(`${API}/stats`),
        axios.get(`${API}/stats/monthly`),
        axios.get(`${API}/events/recent?limit=10`)
      ]);
      setStats(statsRes.data);
      setMonthlyStats(monthlyRes.data.monthly_stats || []);
      setRecentEvents(eventsRes.data.events || []);
    } catch (error) {
      console.error("Dashboard data error:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-heading text-3xl md:text-4xl mb-2">Hoş Geldin, {user.name?.split(" ")[0]}!</h1>
        <p className="text-muted-foreground">KAHU platformunun genel durumu</p>
      </div>

      {/* Stats Grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-card border border-border/50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Toplam Can</p>
                <p className="font-heading text-3xl mt-1">{stats?.total_dogs || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Dog className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border border-border/50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Sahiplendirilen</p>
                <p className="font-heading text-3xl mt-1">{stats?.adopted_dogs || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center">
                <Heart className="w-6 h-6 text-accent" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border border-border/50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Koruyucu Aile</p>
                <p className="font-heading text-3xl mt-1">{stats?.active_guardians || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-secondary/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-secondary" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border border-border/50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Bekleyen Başvuru</p>
                <p className="font-heading text-3xl mt-1">{stats?.pending_applications || 0}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-destructive/10 flex items-center justify-center">
                <FileText className="w-6 h-6 text-destructive" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="bg-card border border-border/50">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Aylık Sahiplendirme
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={monthlyStats}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip 
                  contentStyle={{ 
                    background: "hsl(var(--card))", 
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px"
                  }} 
                />
                <Bar dataKey="adoptions" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="bg-card border border-border/50">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <HandHeart className="w-5 h-5 text-accent" />
              Aylık Bağışlar
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={monthlyStats}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <YAxis tick={{ fontSize: 12 }} stroke="hsl(var(--muted-foreground))" />
                <Tooltip 
                  contentStyle={{ 
                    background: "hsl(var(--card))", 
                    border: "1px solid hsl(var(--border))",
                    borderRadius: "8px"
                  }} 
                />
                <Line type="monotone" dataKey="donations" stroke="hsl(var(--accent))" strokeWidth={2} dot={{ fill: "hsl(var(--accent))" }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Recent Events */}
      <Card className="bg-card border border-border/50">
        <CardHeader>
          <CardTitle className="font-heading text-xl flex items-center gap-2">
            <Calendar className="w-5 h-5 text-secondary" />
            Son Olaylar
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recentEvents.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">Henüz olay kaydı yok</p>
          ) : (
            <div className="space-y-3">
              {recentEvents.map((event) => (
                <div key={event.event_id} className="flex items-center gap-4 p-3 bg-muted/30 rounded-xl">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <PawPrint className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{event.dog_name}</p>
                    <p className="text-sm text-muted-foreground">{event.notes || event.event_type}</p>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {new Date(event.created_at).toLocaleDateString("tr-TR")}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Dogs Management Component - Full Admin Panel
function DogsManagement({ user }) {
  const [dogs, setDogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showBulkImportDialog, setShowBulkImportDialog] = useState(false);
  const [selectedDog, setSelectedDog] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [googleSheetUrl, setGoogleSheetUrl] = useState("");
  const [updateReminders, setUpdateReminders] = useState({});
  const [previewDog, setPreviewDog] = useState(null);
  const [previewPhotos, setPreviewPhotos] = useState([]);
  const [enhancingField, setEnhancingField] = useState(null);
  const [dogVideos, setDogVideos] = useState([]);
  const [videoUploading, setVideoUploading] = useState(false);
  const [videoLinkInput, setVideoLinkInput] = useState("");
  const videoInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const photoInputRef = useRef(null);
  
  const emptyDog = {
    name: "",
    gender: "male",
    size: "medium",
    breed: "",
    breed_en: "",
    microchip_number: "",
    ear_tag_number: "",
    approx_birth_date: "",
    intake_source_type: "sokak",
    intake_source_detail: "",
    status: "sahiplendirmeye_hazir",
    story_short: "",
    story_short_en: "",
    story_long: "",
    story_long_en: "",
    photo_url: "",
    photos_base64: [],
    video_urls: [],
    is_child_friendly: false,
    is_dog_friendly: false,
    is_cat_friendly: false,
    is_apartment_suitable: false,
    is_neutered: "",
    vaccination_status: "",
    vaccinations: [],
    parasite_treatment: "",
    health_notes: "",
    weight: "",
    vet_name: "",
    vet_clinic: "",
    // Offline sahiplendirme (sadece status === "sahiplendirildi" ise gönderilir)
    offline_adopter_name: "",
    offline_adopter_email: "",
    offline_adopter_phone: "",
    offline_adoption_notes: ""
  };
  
  const [dogForm, setDogForm] = useState(emptyDog);

  useEffect(() => {
    fetchDogs();
    fetchUpdateReminders();
  }, []);

  // Normalize FastAPI/Pydantic error detail (may be List[dict]) for use in toast/UI.
  // Prevents "Objects are not valid as a React child" crash from raw array toasts.
  const _extractApiError = (error, fallback) => {
    const d = error?.response?.data?.detail;
    if (Array.isArray(d)) return d.map(e => e?.msg || String(e)).join(", ");
    if (typeof d === "string") return d;
    if (d && typeof d === "object") return d.msg || fallback;
    return fallback;
  };

  const fetchDogs = async () => {
    try {
      // Admin panel needs ALL dogs including sahiplendirildi/vefat/kayip.
      const response = await axios.get(`${API}/dogs?limit=200&admin_view=true`);
      setDogs(response.data.dogs);
    } catch (error) {
      console.error("Dogs fetch error:", error);
      toast.error("Canlar yuklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const fetchUpdateReminders = async () => {
    try {
      const res = await axios.get(`${API}/admin/dogs/update-reminders`, { withCredentials: true });
      const map = {};
      (res.data.reminders || []).forEach(r => { map[r.dog_id] = r.days_since_update; });
      setUpdateReminders(map);
    } catch (e) {
      console.error("Reminders fetch error:", e);
    }
  };

  const handleAddDog = async () => {
    if (!dogForm.name.trim()) {
      toast.error("İsim zorunludur");
      return;
    }
    try {
      const { photo_url, intake_date, ...formData } = dogForm;
      // Strip empty strings — FastAPI Optional[EmailStr]/date fields reject '' (only accept None or valid).
      Object.keys(formData).forEach(k => {
        if (formData[k] === "" || formData[k] === null) delete formData[k];
      });
      // Numeric field coercion
      if (formData.weight !== undefined) formData.weight = parseFloat(formData.weight);
      // Vaccinations list cleanup
      if (formData.vaccinations) {
        formData.vaccinations = formData.vaccinations.filter(v => v?.name?.trim());
        if (formData.vaccinations.length === 0) delete formData.vaccinations;
      }
      await axios.post(`${API}/dogs`, formData);
      toast.success("Yeni can eklendi!");
      setShowAddDialog(false);
      setDogForm(emptyDog);
      fetchDogs();
    } catch (error) {
      toast.error(_extractApiError(error, "Can eklenemedi"));
    }
  };

  const openEditDialog = async (dog) => {
    setSelectedDog(dog);
    // Load existing photos from dog_photos collection
    let existingPhotos = [];
    try {
      const res = await axios.get(`${API}/dogs/${dog.dog_id}/photos`);
      existingPhotos = (res.data.photos || []).map(p => p.media_base64 || (p.image_base64 ? `data:image/jpeg;base64,${p.image_base64}` : null)).filter(Boolean);
    } catch (e) { console.error(e); }
    
    // Load existing videos
    let existingVideos = [];
    try {
      const vres = await axios.get(`${API}/dogs/${dog.dog_id}/videos`);
      existingVideos = vres.data.videos || [];
    } catch (e) { console.error(e); }
    setDogVideos(existingVideos);
    setVideoLinkInput("");
    
    setDogForm({
      name: dog.name || "",
      gender: dog.gender || "male",
      size: dog.size || "medium",
      breed: dog.breed || "",
      breed_en: dog.breed_en || "",
      microchip_number: dog.microchip_number || "",
      ear_tag_number: dog.ear_tag_number || "",
      approx_birth_date: dog.approx_birth_date || "",
      intake_source_type: dog.intake_source_type || "sokak",
      intake_source_detail: dog.intake_source_detail || "",
      intake_date: dog.intake_date || "",
      status: dog.status || "barinakta",
      story_short: dog.story_short || "",
      story_short_en: dog.story_short_en || "",
      story_long: dog.story_long || "",
      story_long_en: dog.story_long_en || "",
      photo_url: "",
      photos_base64: existingPhotos.length > 0 ? existingPhotos : (dog.photo_url ? [dog.photo_url] : []),
      is_child_friendly: dog.is_child_friendly || false,
      is_dog_friendly: dog.is_dog_friendly || false,
      is_cat_friendly: dog.is_cat_friendly || false,
      is_apartment_suitable: dog.is_apartment_suitable || false,
      is_neutered: dog.is_neutered || "",
      vaccination_status: dog.vaccination_status || "",
      vaccinations: dog.vaccinations || [],
      parasite_treatment: dog.parasite_treatment || "",
      health_notes: dog.health_notes || "",
      weight: dog.weight || "",
      vet_name: dog.vet_name || "",
      vet_clinic: dog.vet_clinic || "",
      video_urls: []
    });
    setShowEditDialog(true);
  };

  const handleUpdateDog = async () => {
    if (!selectedDog) return;
    try {
      const { photo_url, ...formData } = dogForm;
      // Sanitize medical fields
      if (formData.weight === "" || formData.weight === null) delete formData.weight;
      else formData.weight = parseFloat(formData.weight);
      if (!formData.is_neutered) delete formData.is_neutered;
      if (!formData.vaccination_status) delete formData.vaccination_status;
      if (!formData.parasite_treatment) delete formData.parasite_treatment;
      if (!formData.health_notes) delete formData.health_notes;
      if (!formData.vet_name) delete formData.vet_name;
      if (!formData.vet_clinic) delete formData.vet_clinic;
      if (!formData.vaccinations || formData.vaccinations.length === 0) delete formData.vaccinations;
      else formData.vaccinations = formData.vaccinations.filter(v => v.name.trim());
      await axios.put(`${API}/admin/dogs/${selectedDog.dog_id}`, formData);
      toast.success("Can güncellendi!");
      setShowEditDialog(false);
      setSelectedDog(null);
      setDogForm(emptyDog);
      fetchDogs();
    } catch (error) {
      toast.error(_extractApiError(error, "Güncelleme başarısız"));
    }
  };

  const handleEnhanceStory = async (field) => {
    const draft = field === "story_short" ? dogForm.story_short : dogForm.story_long;
    if (!draft || !draft.trim()) {
      toast.error("Geliştirmek için önce birkaç cümle yazın");
      return;
    }
    setEnhancingField(field);
    try {
      const dogId = selectedDog?.dog_id;
      const payload = { draft, field };
      let res;
      if (dogId) {
        res = await axios.post(`${API}/dogs/${dogId}/enhance-story`, payload);
      } else {
        res = await axios.post(`${API}/enhance-story`, {
          ...payload,
          dog_name: dogForm.name || undefined,
          dog_gender: dogForm.gender || undefined,
          dog_size: dogForm.size || undefined,
          dog_breed: dogForm.breed || undefined
        });
      }
      setDogForm(prev => ({ ...prev, [field]: res.data.enhanced_text }));
      toast.success(field === "story_short" ? "Kısa hikaye geliştirildi!" : "Detaylı hikaye geliştirildi!");
    } catch (error) {
      toast.error(_extractApiError(error, "AI ile geliştirme başarısız"));
    } finally {
      setEnhancingField(null);
    }
  };

  // Translate TR dog fields → EN via GPT-5, persist to DB, then patch local form state.
  const handleTranslateDogEN = async (overwrite = false) => {
    if (!selectedDog?.dog_id) {
      toast.error("Önce köpeği kaydedin, sonra çeviri yapabilirsiniz");
      return;
    }
    setEnhancingField("translate_en");
    try {
      const res = await axios.post(
        `${API}/admin/dogs/${selectedDog.dog_id}/translate-en`,
        { overwrite },
      );
      const values = res.data?.values || {};
      if (Object.keys(values).length === 0) {
        toast.info(res.data?.message || "Çevrilecek boş EN alanı yok");
        return;
      }
      setDogForm(prev => ({
        ...prev,
        ...(values.story_short ? { story_short_en: values.story_short } : {}),
        ...(values.story_long ? { story_long_en: values.story_long } : {}),
        ...(values.breed ? { breed_en: values.breed } : {}),
      }));
      toast.success(`${res.data.translated} alan İngilizce'ye çevrildi`);
    } catch (error) {
      toast.error(_extractApiError(error, "EN çeviri başarısız"));
    } finally {
      setEnhancingField(null);
    }
  };

  const handleVideoUpload = async (e, dogId) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) { toast.error("Video 50MB'den küçük olmalı"); return; }
    if (dogVideos.length >= 3) { toast.error("Maksimum 3 video"); return; }
    setVideoUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/dogs/${dogId}/videos/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      setDogVideos(prev => [...prev, { video_id: res.data.video_id, url: res.data.url, type: "upload" }]);
      toast.success("Video yüklendi!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Video yüklenemedi");
    } finally {
      setVideoUploading(false);
      e.target.value = "";
    }
  };

  const handleAddVideoLink = async (dogId) => {
    if (!videoLinkInput.trim()) return;
    if (dogVideos.length >= 3) { toast.error("Maksimum 3 video"); return; }
    try {
      const res = await axios.post(`${API}/dogs/${dogId}/videos/link`, { url: videoLinkInput.trim() });
      setDogVideos(prev => [...prev, { video_id: res.data.video_id, url: res.data.url, type: res.data.type }]);
      setVideoLinkInput("");
      toast.success("Video linki eklendi!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Link eklenemedi");
    }
  };

  const handleDeleteVideo = async (dogId, videoId) => {
    try {
      await axios.delete(`${API}/dogs/${dogId}/videos/${videoId}`);
      setDogVideos(prev => prev.filter(v => v.video_id !== videoId));
      toast.success("Video silindi");
    } catch (err) {
      toast.error("Video silinemedi");
    }
  };

  const openPreview = async (dog) => {
    setPreviewDog(dog);
    try {
      const res = await axios.get(`${API}/dogs/${dog.dog_id}/photos`);
      setPreviewPhotos(res.data.photos || []);
    } catch { setPreviewPhotos([]); }
  };

  const openDeleteDialog = (dog) => {
    setSelectedDog(dog);
    setShowDeleteDialog(true);
  };

  const handleDeleteDog = async () => {
    if (!selectedDog) return;
    try {
      await axios.delete(`${API}/admin/dogs/${selectedDog.dog_id}`);
      toast.success(`${selectedDog.name} silindi`);
      setShowDeleteDialog(false);
      setSelectedDog(null);
      fetchDogs();
    } catch (error) {
      toast.error("Silme işlemi başarısız");
    }
  };

  const handleStatusChange = async (dogId, newStatus) => {
    try {
      await axios.post(`${API}/admin/dogs/${dogId}/status?new_status=${newStatus}`);
      toast.success("Durum güncellendi");
      fetchDogs();
    } catch (error) {
      const detail = error?.response?.data?.detail || "Durum güncellenemedi";
      toast.error(detail);
    }
  };

  // Bulk Import Functions
  const handleGoogleSheetFetch = async () => {
    const url = googleSheetUrl.trim();
    if (!url) return;
    try {
      const res = await axios.get(`${API}/admin/dogs/fetch-google-sheet`, { params: { url } });
      const csv = res.data?.csv;
      if (!csv) {
        toast.error("Google Sheets yanıtı boş");
        return;
      }
      // Wrap as a File and reuse the CSV path
      const blob = new Blob([csv], { type: "text/csv" });
      const file = new File([blob], "google-sheets-import.csv", { type: "text/csv" });
      await handleFileSelect({ target: { files: [file] } });
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Google Sheets içe aktarma hatası";
      toast.error(msg, { duration: 10000 });
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    setImportResults(null);
    setImportPreview(null);
    
    try {
      let dogsData = [];
      
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        // Parse Excel with SheetJS
        const xlsxModule = await import('xlsx');
        const XLSX = xlsxModule.default || xlsxModule;
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
        
        if (rows.length === 0) {
          toast.error("Excel dosyası boş");
          return;
        }
        
        console.log("Excel headers:", Object.keys(rows[0]));
        console.log("First row:", rows[0]);
        
        for (const row of rows) {
          const dog = {};
          Object.entries(row).forEach(([header, value]) => {
            // Normalize: lowercase, trim, replace Turkish chars, replace spaces with underscore
            const h = header.trim().toLowerCase()
              .replace(/ş/g,'s').replace(/ı/g,'i').replace(/ö/g,'o')
              .replace(/ü/g,'u').replace(/ç/g,'c').replace(/ğ/g,'g')
              .replace(/İ/gi,'i').replace(/Ş/gi,'s').replace(/Ö/gi,'o')
              .replace(/Ü/gi,'u').replace(/Ç/gi,'c').replace(/Ğ/gi,'g')
              .replace(/\s+/g, '_');
            const mappings = {
              'isim': 'name', 'ad': 'name', 'name': 'name', 'kopek_adi': 'name',
              'mikrocip': 'microchip_number', 'mikrocip_no': 'microchip_number', 'microchip': 'microchip_number', 'chip': 'microchip_number',
              'kulak_kupesi': 'ear_tag_number', 'kulak': 'ear_tag_number',
              'cinsiyet': 'gender', 'gender': 'gender',
              'boyut': 'size', 'size': 'size',
              'irk': 'breed', 'breed': 'breed',
              'kaynak': 'intake_source_type', 'source': 'intake_source_type',
              'kaynak_detay': 'intake_source_detail', 'kaynak_detayi': 'intake_source_detail',
              'hikaye': 'story_short', 'story': 'story_short', 'kisa_hikaye': 'story_short',
              'dogum': 'approx_birth_date', 'birth': 'approx_birth_date', 'dogum_tarihi': 'approx_birth_date',
              'foto': 'photo_url', 'photo': 'photo_url', 'resim': 'photo_url',
              'durum': 'status', 'status': 'status',
              'kisirlastirma': 'is_neutered', 'kisirlas_tirma': 'is_neutered', 'kisir': 'is_neutered', 'neutered': 'is_neutered',
              'asi_durumu': 'vaccination_status',
              'parazit_tedavisi': 'parasite_treatment', 'parazit': 'parasite_treatment',
              'kilo': 'weight', 'weight': 'weight',
              'saglik_notlari': 'health_notes', 'saglik': 'health_notes',
              'veteriner_adi': 'vet_name', 'veteriner': 'vet_name',
              'klinik': 'vet_clinic', 'vet_clinic': 'vet_clinic',
              'asi_1_adi': 'asi_1_adi', 'asi_1_tarih': 'asi_1_tarih',
              'asi_2_adi': 'asi_2_adi', 'asi_2_tarih': 'asi_2_tarih',
              'asi_3_adi': 'asi_3_adi', 'asi_3_tarih': 'asi_3_tarih',
            };
            const field = mappings[h] || h;
            const strVal = String(value).trim();
            if (strVal !== '' && strVal !== 'undefined') dog[field] = strVal;
          });
          
          // If no 'name' found, try first column as name
          if (!dog.name) {
            const firstKey = Object.keys(row)[0];
            const firstVal = String(row[firstKey]).trim();
            if (firstVal) dog.name = firstVal;
          }
          
          // Skip header/label rows (name equals a known header keyword)
          const skipNames = ['isim', 'ad', 'name', 'İsim', 'isım', 'AD', 'NAME'];
          if (dog.name && !skipNames.includes(dog.name.trim())) {
            const genderMap = { 'erkek': 'male', 'dişi': 'female', 'disi': 'female', 'bilinmiyor': 'unknown' };
            if (dog.gender && genderMap[dog.gender.toLowerCase()]) dog.gender = genderMap[dog.gender.toLowerCase()];
            const sizeMap = { 'küçük': 'small', 'kucuk': 'small', 'orta': 'medium', 'büyük': 'large', 'buyuk': 'large', 'dev': 'giant' };
            if (dog.size && sizeMap[dog.size.toLowerCase()]) dog.size = sizeMap[dog.size.toLowerCase()];
            dogsData.push(dog);
          }
        }
      } else {
        // CSV or JSON - read as text
        const text = await file.text();
        
        if (file.name.endsWith('.csv')) {
          // Strip UTF-8 BOM if present
          const cleanText = text.replace(/^\uFEFF/, '');
          const lines = cleanText.split(/\r?\n/).filter(line => line.trim());
          if (lines.length < 2) {
            toast.error("CSV dosyası boş veya geçersiz");
            return;
          }

          // Auto-detect delimiter: ; (Turkish Excel default), , or tab
          const firstLine = lines[0];
          const counts = {
            ';': (firstLine.match(/;/g) || []).length,
            ',': (firstLine.match(/,/g) || []).length,
            '\t': (firstLine.match(/\t/g) || []).length,
          };
          let delimiter = ',';
          let maxCount = counts[','];
          for (const [delim, cnt] of Object.entries(counts)) {
            if (cnt > maxCount) { maxCount = cnt; delimiter = delim; }
          }
          console.log(`CSV delimiter detected: "${delimiter}" (counts:`, counts, ')');

          const splitRow = (row) => {
            const values = [];
            let current = '';
            let inQuotes = false;
            for (const ch of row) {
              if (ch === '"') { inQuotes = !inQuotes; }
              else if (ch === delimiter && !inQuotes) { values.push(current.trim()); current = ''; }
              else { current += ch; }
            }
            values.push(current.trim());
            return values;
          };

          const normalizeHeader = (h) => h.trim().toLowerCase()
            .replace(/^\uFEFF/, '')
            .replace(/\s+/g, '_')
            .replace(/ş/g,'s').replace(/ı/g,'i').replace(/ö/g,'o')
            .replace(/ü/g,'u').replace(/ç/g,'c').replace(/ğ/g,'g')
            .replace(/İ/gi,'i');

          const rawHeaders = splitRow(lines[0]).map(normalizeHeader);
          console.log('CSV headers normalized:', rawHeaders);
          
          for (let i = 1; i < lines.length; i++) {
            const values = splitRow(lines[i]);

            const dog = {};
            rawHeaders.forEach((header, idx) => {
              const mappings = {
                'isim': 'name', 'ad': 'name', 'name': 'name', 'kopek_adi': 'name',
                'mikrocip': 'microchip_number', 'mikrocip_no': 'microchip_number', 'microchip': 'microchip_number', 'chip': 'microchip_number',
                'kulak_kupesi': 'ear_tag_number', 'kulak': 'ear_tag_number',
                'cinsiyet': 'gender', 'gender': 'gender',
                'boyut': 'size', 'size': 'size',
                'irk': 'breed', 'breed': 'breed',
                'kaynak': 'intake_source_type', 'source': 'intake_source_type',
                'kaynak_detay': 'intake_source_detail', 'kaynak_detayi': 'intake_source_detail',
                'hikaye': 'story_short', 'story': 'story_short', 'kisa_hikaye': 'story_short',
                'dogum': 'approx_birth_date', 'birth': 'approx_birth_date', 'dogum_tarihi': 'approx_birth_date',
                'foto': 'photo_url', 'photo': 'photo_url', 'resim': 'photo_url',
                'durum': 'status', 'status': 'status',
                'kisirlastirma': 'is_neutered', 'kisirlas_tirma': 'is_neutered', 'kisir': 'is_neutered', 'neutered': 'is_neutered',
                'asi_durumu': 'vaccination_status', 'vaccination_status': 'vaccination_status',
                'parazit_tedavisi': 'parasite_treatment', 'parazit': 'parasite_treatment',
                'kilo': 'weight', 'weight': 'weight',
                'saglik_notlari': 'health_notes', 'saglik': 'health_notes', 'health_notes': 'health_notes',
                'veteriner_adi': 'vet_name', 'veteriner': 'vet_name', 'vet_name': 'vet_name',
                'klinik': 'vet_clinic', 'vet_clinic': 'vet_clinic',
                'asi_1_adi': 'asi_1_adi', 'asi_1_tarih': 'asi_1_tarih',
                'asi_2_adi': 'asi_2_adi', 'asi_2_tarih': 'asi_2_tarih',
                'asi_3_adi': 'asi_3_adi', 'asi_3_tarih': 'asi_3_tarih',
                'asi_4_adi': 'asi_4_adi', 'asi_4_tarih': 'asi_4_tarih',
                'asi_5_adi': 'asi_5_adi', 'asi_5_tarih': 'asi_5_tarih',
              };
              const field = mappings[header] || header;
              const val = (values[idx] || '').trim();
              if (val !== '' && val.toLowerCase() !== 'undefined') dog[field] = val;
            });
            if (dog.name) {
              const skipNames = ['isim', 'ad', 'name', 'İsim', 'isım', 'AD', 'NAME'];
              if (skipNames.includes(dog.name.trim())) continue;
              const genderMap = { 'erkek': 'male', 'dişi': 'female', 'disi': 'female', 'bilinmiyor': 'unknown' };
              if (dog.gender && genderMap[dog.gender.toLowerCase()]) dog.gender = genderMap[dog.gender.toLowerCase()];
              const sizeMap = { 'küçük': 'small', 'kucuk': 'small', 'orta': 'medium', 'büyük': 'large', 'buyuk': 'large', 'dev': 'giant' };
              if (dog.size && sizeMap[dog.size.toLowerCase()]) dog.size = sizeMap[dog.size.toLowerCase()];
              dogsData.push(dog);
            }
          }
        } else if (file.name.endsWith('.json')) {
          const parsed = JSON.parse(text);
          dogsData = Array.isArray(parsed) ? parsed : parsed.dogs || [];
        } else {
          toast.error("Desteklenen format: CSV, Excel veya JSON");
          return;
        }
      }
      
      if (dogsData.length === 0) {
        const fileName = file.name;
        if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
          const xlsxModule2 = await import('xlsx');
          const XLSX2 = xlsxModule2.default || xlsxModule2;
          const buf = await file.arrayBuffer();
          const wb = XLSX2.read(buf, { type: 'array' });
          const sh = wb.Sheets[wb.SheetNames[0]];
          const rw = XLSX2.utils.sheet_to_json(sh, { defval: '' });
          const detectedHeaders = rw.length > 0 ? Object.keys(rw[0]).join(', ') : 'boş';
          toast.error(`Veri bulunamadı. Algılanan sütunlar: ${detectedHeaders}`);
        } else if (fileName.endsWith('.csv')) {
          const raw = (await file.text()).replace(/^\uFEFF/, '').split(/\r?\n/)[0] || '';
          toast.error(`İçe aktarılacak veri bulunamadı. Başlık satırı: "${raw.slice(0, 120)}${raw.length > 120 ? '…' : ''}". İlk sütun 'isim' olmalı. (Türkçe Excel ; ayracını kullanırsa otomatik algılanır.)`);
        } else {
          toast.error("İçe aktarılacak veri bulunamadı. İlk sütun 'isim' olmalı.");
        }
        return;
      }
      
      // Client-side validation: duplicate microchip numbers in the same file
      const chipCounts = {};
      dogsData.forEach((d, i) => {
        const mc = (d.microchip_number || '').trim();
        if (mc) {
          if (!chipCounts[mc]) chipCounts[mc] = [];
          chipCounts[mc].push({ row: i + 2, name: d.name });
        }
      });
      const dupes = Object.entries(chipCounts).filter(([, rows]) => rows.length > 1);
      if (dupes.length > 0) {
        const dupMsg = dupes.map(([mc, rows]) =>
          `• ${mc}: ${rows.map(r => `${r.name} (satır ${r.row})`).join(', ')}`
        ).join('\n');
        toast.error(
          `Dosyada ${dupes.length} tekrarlanan mikroçip numarası var.\n${dupMsg}\n\n` +
          `⚠️ Excel bu 15 haneli sayıyı bilimsel notasyonda (örn. 1,23E+14) gösterebilir — ` +
          `bu yüzden farklı görünseler de aynı sayı olabilirler. Excel'de chip kolonunu seçip ` +
          `'Hücre Biçimlendirme → Metin' yaparak gerçek değerleri kontrol edin.`,
          { duration: 15000 }
        );
        return;
      }

      // Step 1: Dry run (preview) - validate without committing
      const previewRes = await axios.post(`${API}/admin/dogs/bulk-import?dry_run=true`, { dogs: dogsData });
      setImportPreview({ preview: previewRes.data, dogsData });
    } catch (err) {
      console.error("Bulk import error:", err);
      toast.error(err.response?.data?.detail || err.message || "İçe aktarma hatası");
    } finally {
      setImporting(false);
    }
  };

  const confirmBulkImport = async (onlyMode = null) => {
    if (!importPreview?.dogsData) return;
    setImporting(true);
    try {
      const params = onlyMode ? `?only=${onlyMode}` : '';
      const response = await axios.post(`${API}/admin/dogs/bulk-import${params}`, { dogs: importPreview.dogsData });
      setImportResults(response.data);
      setImportPreview(null);
      const newCount = onlyMode === 'update' ? 0 : response.data.created_count;
      const upCount = onlyMode === 'create' ? 0 : response.data.updated_count;
      if (newCount > 0) toast.success(`${newCount} yeni can oluşturuldu`);
      if (upCount > 0) toast.success(`${upCount} can güncellendi`);
      if (newCount === 0 && upCount === 0 && response.data.error_count === 0) {
        toast.info("Kaydedilen bir şey yok");
      }
      if (response.data.error_count > 0) {
        const firstFew = (response.data.errors || []).slice(0, 3).map(e => `Satır ${e.row}: ${e.error}`).join('\n');
        const more = response.data.error_count > 3 ? `\n…ve ${response.data.error_count - 3} hata daha` : '';
        toast.error(`${response.data.error_count} satırda hata:\n${firstFew}${more}`, { duration: 12000 });
      }
      fetchDogs();
    } catch (error) {
      console.error("Import error:", error);
      toast.error("İçe aktarma başarısız: " + (error.message || "Dosya formatı hatalı"));
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    // Geçerli 10 durum: barinakta, tedavi_goruyor, sahiplendirmeye_hazir,
    // rezerve, sahiplendirildi, koruyucu_ailede, transfer, iade, kayip, vefat.
    // "Diğer" kategorisi denetim güvenliği için sistemden kaldırılmıştır.
    const csv = `isim,mikrocip,kulak_kupesi,cinsiyet,boyut,irk,durum,kaynak,kaynak_detay,dogum,hikaye,kisirlastirma,asi_durumu,parazit_tedavisi,kilo,saglik_notlari,veteriner_adi,klinik,asi_1_adi,asi_1_tarih,asi_2_adi,asi_2_tarih,asi_3_adi,asi_3_tarih
Buddy,900108000000001,,erkek,orta,Golden Mix,sahiplendirmeye_hazir,sokak,,2023-06-15,Çok sevimli bir dost,evet,tam,yapildi,12.5,Sağlıklı,Dr. Yılmaz,Pati Klinik,Kuduz,2025-06-15,Karma,2025-07-01,,
Daisy,900108000000002,KK-001,dişi,küçük,Karışık,tedavi_goruyor,devlet_barinak,Ankara Barınağı,2024-01-10,Sakin ve uyumlu,hayir,eksik,yapildi,8.2,Alerjik dermatit tedavisi,Dr. Kaya,Umut Klinik,Kuduz,2025-03-20,,,,
Karabaş,900108000000003,,erkek,büyük,Kangal,koruyucu_ailede,kirsal,,2022-04-15,Sürü bekçisi Karabaş,evet,tam,yapildi,32.0,Sağlıklı,,,Kuduz,2025-02-10,Karma,2025-03-15,,`;
    const blob = new Blob(["\uFEFF" + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'kahu_toplu_import_sablonu.csv';
    a.click();
  };

  const downloadTemplateXlsx = async () => {
    try {
      const response = await axios.get(`${API}/admin/dogs/import-template.xlsx`, { responseType: "blob" });
      const blob = new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "kahu_toplu_import_sablonu.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("xlsx template error:", e);
      toast.error("Excel şablonu indirilemedi");
    }
  };

  // Filters
  const filteredDogs = dogs.filter(dog => {
    const matchesSearch = dog.name?.toLowerCase().includes(search.toLowerCase()) ||
      dog.microchip_number?.toLowerCase().includes(search.toLowerCase()) ||
      dog.breed?.toLowerCase().includes(search.toLowerCase());
    // Status filtresi — mutually-exclusive statü karşılaştırması.
    // "anomaly": bilinen 10 statünün DIŞINDA kalan kayıtlar (data drift alarmı).
    //            Bu kayıtlar sistemden geldiği anda flag'lenir; UI'da kırmızı ile
    //            gösterilir. Sistemden bilinçli "Diğer" kategorisi kaldırıldı.
    // "all":     filtresiz
    const knownStatuses = ["barinakta","tedavi_goruyor","sahiplendirmeye_hazir","rezerve","sahiplendirildi","koruyucu_ailede","transfer","iade","kayip","vefat"];
    let matchesStatus;
    if (statusFilter === "all") matchesStatus = true;
    else if (statusFilter === "sponsored") matchesStatus = (dog.guardian_active_count || 0) > 0;
    else if (statusFilter === "anomaly") matchesStatus = !knownStatuses.includes(dog.status);
    else matchesStatus = dog.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const styles = {
      barinakta: "bg-slate-100 text-slate-700 border-slate-200",
      tedavi_goruyor: "bg-rose-100 text-rose-700 border-rose-200",
      sahiplendirmeye_hazir: "bg-green-100 text-green-700 border-green-200",
      sahiplendirildi: "bg-blue-100 text-blue-700 border-blue-200",
      koruyucu_ailede: "bg-purple-100 text-purple-700 border-purple-200",
      rezerve: "bg-amber-100 text-amber-700 border-amber-200",
      transfer: "bg-indigo-100 text-indigo-700 border-indigo-200",
      iade: "bg-orange-100 text-orange-700 border-orange-200",
      kayip: "bg-red-100 text-red-700 border-red-200",
      vefat: "bg-gray-100 text-gray-500 border-gray-200",
    };
    const labels = {
      barinakta: "Alanda",
      tedavi_goruyor: "Tedavi Görüyor",
      sahiplendirmeye_hazir: "Sahiplenmeye Hazır",
      sahiplendirildi: "Sahiplendirildi",
      koruyucu_ailede: "Geçici Bakımda",
      rezerve: "Rezerve",
      transfer: "Transfer",
      iade: "İade",
      kayip: "Kayıp",
      vefat: "Vefat",
    };
    return <Badge className={`${styles[status] || "bg-red-100 text-red-800 border-red-300"} border`}>{labels[status] || `⚠ ${status || "boş"}`}</Badge>;
  };

  const statusCounts = {
    all: dogs.length,
    barinakta: dogs.filter(d => d.status === "barinakta").length,
    tedavi_goruyor: dogs.filter(d => d.status === "tedavi_goruyor").length,
    sahiplendirmeye_hazir: dogs.filter(d => d.status === "sahiplendirmeye_hazir").length,
    rezerve: dogs.filter(d => d.status === "rezerve").length,
    sahiplendirildi: dogs.filter(d => d.status === "sahiplendirildi").length,
    koruyucu_ailede: dogs.filter(d => d.status === "koruyucu_ailede").length,
    transfer: dogs.filter(d => d.status === "transfer").length,
    iade: dogs.filter(d => d.status === "iade").length,
    kayip: dogs.filter(d => d.status === "kayip").length,
    vefat: dogs.filter(d => d.status === "vefat").length,
    // Beklenmeyen statü (data drift kontrolü). Sistemden "Diğer" kategorisi
    // KALDIRILDI — bu sayaç yalnızca beklenmeyen statü sızarsa anomali
    // sekmesinde kırmızı olarak gösterilir.
    sponsored: dogs.filter(d => (d.guardian_active_count || 0) > 0).length,
    anomaly: dogs.filter(d => !["barinakta","tedavi_goruyor","sahiplendirmeye_hazir","rezerve","sahiplendirildi","koruyucu_ailede","transfer","iade","kayip","vefat"].includes(d.status)).length,
  };

  // Dog Form JSX (inline, not a component — prevents focus loss on re-render)
  const dogFormFields = (
    <div className="space-y-4 mt-4 max-h-[70vh] overflow-y-auto pr-2">
      {/* Basic Info */}
      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label>İsim *</Label>
          <Input
            value={dogForm.name}
            onChange={(e) => setDogForm({ ...dogForm, name: e.target.value })}
            className="mt-1.5"
            placeholder="Can ismi"
          />
        </div>
        <div>
          <Label>Mikroçip No (Benzersiz ID)</Label>
          <Input
            value={dogForm.microchip_number}
            onChange={(e) => setDogForm({ ...dogForm, microchip_number: e.target.value })}
            className="mt-1.5"
            placeholder="15 haneli mikroçip numarası"
          />
        </div>
        <div>
          <Label>Kulak Küpesi No</Label>
          <Input
            value={dogForm.ear_tag_number}
            onChange={(e) => setDogForm({ ...dogForm, ear_tag_number: e.target.value })}
            className="mt-1.5"
            placeholder="Varsa kulak küpesi"
          />
        </div>
      </div>

      {/* Physical */}
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label>Cinsiyet</Label>
          <Select value={dogForm.gender} onValueChange={(v) => setDogForm({ ...dogForm, gender: v })}>
            <SelectTrigger className="mt-1.5">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="male">Erkek</SelectItem>
              <SelectItem value="female">Dişi</SelectItem>
              <SelectItem value="unknown">Bilinmiyor</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Boyut</Label>
          <Select value={dogForm.size} onValueChange={(v) => setDogForm({ ...dogForm, size: v })}>
            <SelectTrigger className="mt-1.5">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="small">Küçük</SelectItem>
              <SelectItem value="medium">Orta</SelectItem>
              <SelectItem value="large">Büyük</SelectItem>
              <SelectItem value="giant">Dev</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Tahmini Doğum</Label>
          <Input
            type="date"
            value={dogForm.approx_birth_date}
            onChange={(e) => setDogForm({ ...dogForm, approx_birth_date: e.target.value })}
            className="mt-1.5"
          />
        </div>
      </div>

      <div>
        <Label>Irk</Label>
        <Input
          value={dogForm.breed}
          onChange={(e) => setDogForm({ ...dogForm, breed: e.target.value })}
          className="mt-1.5"
          placeholder="Karışık veya ırk adı"
        />
        <div className="mt-2 flex items-center gap-2">
          <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 shrink-0">EN</span>
          <Input
            value={dogForm.breed_en || ""}
            onChange={(e) => setDogForm({ ...dogForm, breed_en: e.target.value })}
            placeholder="Breed (English) — optional"
            className="text-sm"
            data-testid="dog-breed-en-input"
          />
        </div>
      </div>

      {/* Status & Source */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Durum</Label>
          <Select value={dogForm.status} onValueChange={(v) => setDogForm({ ...dogForm, status: v })}>
            <SelectTrigger className="mt-1.5" data-testid="dog-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="barinakta">Alanda</SelectItem>
              <SelectItem value="tedavi_goruyor">Tedavi Görüyor</SelectItem>
              <SelectItem value="sahiplendirmeye_hazir">Sahiplenmeye Hazır</SelectItem>
              <SelectItem value="rezerve">Rezerve</SelectItem>
              <SelectItem value="sahiplendirildi">Sahiplendirildi</SelectItem>
              <SelectItem value="koruyucu_ailede">Geçici Bakımda</SelectItem>
              <SelectItem value="transfer">Transfer</SelectItem>
              <SelectItem value="iade">İade</SelectItem>
              <SelectItem value="kayip">Kayıp</SelectItem>
              <SelectItem value="vefat">Vefat</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Geliş Kaynağı</Label>
          <Select value={dogForm.intake_source_type} onValueChange={(v) => setDogForm({ ...dogForm, intake_source_type: v })}>
            <SelectTrigger className="mt-1.5">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="sokak">Sokak</SelectItem>
              <SelectItem value="devlet_barinak">Devlet Barınağı</SelectItem>
              <SelectItem value="ozel_barinak">Özel Barınak</SelectItem>
              <SelectItem value="kirsal">Kırsal</SelectItem>
              <SelectItem value="afet">Afet Bölgesi</SelectItem>
              <SelectItem value="diger">Diğer</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Offline Sahiplendirme — durum 'sahiplendirildi' seçildiğinde görünür.
          Doldurulursa platformdan sahiplenilmiş gibi sahiplendirildi maili + 1/3/6 ay
          takip mailleri otomatik tetiklenir. Boş bırakılırsa sadece statü değişir. */}
      {dogForm.status === "sahiplendirildi" && (
        <div className="rounded-xl border-2 border-primary/30 bg-primary/5 p-4 space-y-3" data-testid="offline-adoption-block">
          <div className="flex items-start gap-2">
            <Heart className="w-5 h-5 text-primary mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-sm">Offline Sahiplendirme Kaydı</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Bilgileri doldurursanız sahiplendiriciye otomatik sahiplendirildi maili gider ve 1/3/6 ay sonra &ldquo;Mokoş nasıl?&rdquo; takip mailleri tetiklenir. Boş bırakırsanız sadece statü kaydedilir.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Sahiplendirici Ad Soyad</Label>
              <Input value={dogForm.offline_adopter_name || ""}
                     onChange={(e) => setDogForm({ ...dogForm, offline_adopter_name: e.target.value })}
                     placeholder="örn. Ayşe Yılmaz" className="mt-1"
                     data-testid="offline-adopter-name" />
            </div>
            <div>
              <Label className="text-xs">Telefon</Label>
              <Input value={dogForm.offline_adopter_phone || ""}
                     onChange={(e) => setDogForm({ ...dogForm, offline_adopter_phone: e.target.value })}
                     placeholder="+90 5XX XXX XX XX" className="mt-1"
                     data-testid="offline-adopter-phone" />
            </div>
          </div>
          <div>
            <Label className="text-xs">E-posta <span className="text-muted-foreground">(mail tetiklenmesi için gerekli)</span></Label>
            <Input type="email" value={dogForm.offline_adopter_email || ""}
                   onChange={(e) => setDogForm({ ...dogForm, offline_adopter_email: e.target.value })}
                   placeholder="aile@example.com" className="mt-1"
                   data-testid="offline-adopter-email" />
          </div>
          <div>
            <Label className="text-xs">Not <span className="text-muted-foreground">(opsiyonel — nasıl sahiplenildi?)</span></Label>
            <Input value={dogForm.offline_adoption_notes || ""}
                   onChange={(e) => setDogForm({ ...dogForm, offline_adoption_notes: e.target.value })}
                   placeholder="örn. Pazar buluşmasında tanıştılar, 3 hafta gözlem sonrası alındı" className="mt-1"
                   data-testid="offline-adoption-notes" />
          </div>
        </div>
      )}

      <div>
        <Label>Kaynak Detayı</Label>
        <Input
          value={dogForm.intake_source_detail}
          onChange={(e) => setDogForm({ ...dogForm, intake_source_detail: e.target.value })}
          className="mt-1.5"
          placeholder="Örn: Etimesgut Belediyesi, Gönüllü teslimi..."
        />
      </div>

      {/* Photos Upload (Multiple) */}
      <div>
        <Label>Fotograflar (max 6)</Label>
        <div className="mt-1.5 grid grid-cols-3 gap-2">
          {(dogForm.photos_base64 || []).map((photo, idx) => (
            <div key={idx} className="relative">
              <img src={photo} alt={`Foto ${idx+1}`} className="w-full h-24 object-cover rounded-xl border border-border" />
              <button
                type="button"
                onClick={() => {
                  const updated = [...dogForm.photos_base64];
                  updated.splice(idx, 1);
                  setDogForm({ ...dogForm, photos_base64: updated });
                }}
                className="absolute -top-1.5 -right-1.5 bg-destructive text-destructive-foreground rounded-full w-5 h-5 flex items-center justify-center text-[10px]"
              >X</button>
            </div>
          ))}
          {(dogForm.photos_base64 || []).length < 6 && (
            <div>
              <input
                type="file"
                accept="image/*"
                ref={photoInputRef}
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (file.size > 5 * 1024 * 1024) { toast.error("Dosya 5MB'den kucuk olmali"); return; }
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    setDogForm(prev => ({ ...prev, photos_base64: [...(prev.photos_base64 || []), reader.result] }));
                  };
                  reader.readAsDataURL(file);
                  e.target.value = "";
                }}
              />
              <button
                type="button"
                onClick={() => photoInputRef.current?.click()}
                className="w-full h-24 border-2 border-dashed border-border rounded-xl flex flex-col items-center justify-center gap-1 hover:border-primary/50 transition-colors cursor-pointer"
              >
                <Plus className="w-5 h-5 text-muted-foreground/50" />
                <span className="text-[11px] text-muted-foreground">Ekle</span>
              </button>
            </div>
          )}
        </div>
        {(dogForm.photos_base64 || []).length > 0 && (
          <p className="text-xs text-muted-foreground mt-1">İlk fotoğraf profil fotoğrafı olarak kullanılır. {dogForm.photos_base64.length}/6</p>
        )}
      </div>

      {/* Videos */}
      {selectedDog && (
        <div className="p-4 bg-muted/30 rounded-xl space-y-4">
          <Label className="text-base font-medium flex items-center gap-2">
            <Video className="w-4 h-4" />
            Videolar ({dogVideos.length}/3)
          </Label>

          {/* Existing Videos */}
          {dogVideos.length > 0 && (
            <div className="space-y-2">
              {dogVideos.map((v) => (
                <div key={v.video_id} className="flex items-center gap-2 p-2 bg-background rounded-lg border border-border/50">
                  <Video className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="text-xs text-muted-foreground truncate flex-1">
                    {v.type === "youtube" ? "YouTube" : v.type === "upload" ? v.original_name || "Yüklenen video" : "Harici link"}
                    {v.url && <span className="ml-1 opacity-60">({v.url.substring(0, 40)}...)</span>}
                  </span>
                  <button
                    type="button"
                    onClick={() => handleDeleteVideo(selectedDog.dog_id, v.video_id)}
                    className="text-destructive hover:text-destructive/80 p-1"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {dogVideos.length < 3 && (
            <div className="space-y-3">
              {/* Upload */}
              <div>
                <input
                  type="file"
                  accept="video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm"
                  ref={videoInputRef}
                  className="hidden"
                  onChange={(e) => handleVideoUpload(e, selectedDog.dog_id)}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => videoInputRef.current?.click()}
                  disabled={videoUploading}
                  className="w-full"
                  data-testid="upload-video-btn"
                >
                  <Video className="w-4 h-4 mr-2" />
                  {videoUploading ? "Yükleniyor..." : "Video Dosyası Yükle (MP4, MOV, max 50MB)"}
                </Button>
              </div>

              {/* YouTube / External Link */}
              <div className="flex gap-2">
                <Input
                  value={videoLinkInput}
                  onChange={(e) => setVideoLinkInput(e.target.value)}
                  placeholder="YouTube veya video linki yapıştır..."
                  className="flex-1 text-sm"
                  data-testid="video-link-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleAddVideoLink(selectedDog.dog_id)}
                  disabled={!videoLinkInput.trim()}
                  data-testid="add-video-link-btn"
                >
                  <Link2 className="w-4 h-4 mr-1" /> Ekle
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Stories */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <Label>Kısa Hikaye</Label>
          <div className="flex items-center gap-3">
            {selectedDog?.dog_id && (
              <button
                type="button"
                onClick={() => handleTranslateDogEN(false)}
                disabled={enhancingField === "translate_en"}
                className="text-xs text-blue-700 hover:text-blue-900 flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="translate-dog-en-btn"
                title="GPT-5 ile boş EN alanlarını doldur"
              >
                <Sparkles className="w-3.5 h-3.5" />
                {enhancingField === "translate_en" ? "Çevriliyor..." : "EN'ye Çevir"}
              </button>
            )}
            <button
              type="button"
              onClick={() => handleEnhanceStory("story_short")}
              disabled={enhancingField === "story_short" || !dogForm.story_short?.trim()}
              className="text-xs text-primary hover:text-primary/80 flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="enhance-story-short-btn"
            >
              <Sparkles className="w-3.5 h-3.5" />
              {enhancingField === "story_short" ? "Geliştiriliyor..." : "AI ile Geliştir"}
            </button>
          </div>
        </div>
        <Textarea
          value={dogForm.story_short}
          onChange={(e) => setDogForm({ ...dogForm, story_short: e.target.value })}
          placeholder="2-3 cümlelik tanıtım yazın, AI ile zenginleştirin..."
          rows={2}
        />
        <div className="mt-2 flex items-start gap-2">
          <span className="text-[10px] font-mono px-1.5 py-0.5 mt-2 rounded bg-blue-100 text-blue-700 shrink-0">EN</span>
          <Textarea
            value={dogForm.story_short_en || ""}
            onChange={(e) => setDogForm({ ...dogForm, story_short_en: e.target.value })}
            placeholder="Short story (English) — optional. Use 'EN'ye Çevir' to auto-fill."
            rows={2}
            className="text-sm"
            data-testid="dog-story-short-en-input"
          />
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <Label>Detaylı Hikaye</Label>
          <button
            type="button"
            onClick={() => handleEnhanceStory("story_long")}
            disabled={enhancingField === "story_long" || !dogForm.story_long?.trim()}
            className="text-xs text-primary hover:text-primary/80 flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="enhance-story-long-btn"
          >
            <Sparkles className="w-3.5 h-3.5" />
            {enhancingField === "story_long" ? "Geliştiriliyor..." : "AI ile Geliştir"}
          </button>
        </div>
        <Textarea
          value={dogForm.story_long}
          onChange={(e) => setDogForm({ ...dogForm, story_long: e.target.value })}
          placeholder="Can hakkında birkaç cümle yazın, AI ile detaylandırın..."
          rows={4}
        />
        <div className="mt-2 flex items-start gap-2">
          <span className="text-[10px] font-mono px-1.5 py-0.5 mt-2 rounded bg-blue-100 text-blue-700 shrink-0">EN</span>
          <Textarea
            value={dogForm.story_long_en || ""}
            onChange={(e) => setDogForm({ ...dogForm, story_long_en: e.target.value })}
            placeholder="Long story (English) — optional. Use 'EN'ye Çevir' button above to auto-fill."
            rows={4}
            className="text-sm"
            data-testid="dog-story-long-en-input"
          />
        </div>
      </div>

      {/* Traits */}
      <div className="p-4 bg-muted/30 rounded-xl space-y-3">
        <Label className="text-base font-medium">Özellikler</Label>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex items-center gap-2">
            <Switch
              checked={dogForm.is_child_friendly}
              onCheckedChange={(v) => setDogForm({ ...dogForm, is_child_friendly: v })}
            />
            <Label className="font-normal">Çocuk Dostu</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={dogForm.is_dog_friendly}
              onCheckedChange={(v) => setDogForm({ ...dogForm, is_dog_friendly: v })}
            />
            <Label className="font-normal">Köpek Dostu</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={dogForm.is_cat_friendly}
              onCheckedChange={(v) => setDogForm({ ...dogForm, is_cat_friendly: v })}
            />
            <Label className="font-normal">Kedi Dostu</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={dogForm.is_apartment_suitable}
              onCheckedChange={(v) => setDogForm({ ...dogForm, is_apartment_suitable: v })}
            />
            <Label className="font-normal">Apartmana Uygun</Label>
          </div>
        </div>
      </div>

      {/* Medical Info */}
      <div className="p-4 bg-muted/30 rounded-xl space-y-4">
        <Label className="text-base font-medium flex items-center gap-2">
          <Syringe className="w-4 h-4" />
          Sağlık Bilgileri
        </Label>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Kısırlaştırma</Label>
            <Select value={dogForm.is_neutered || ""} onValueChange={(v) => setDogForm({ ...dogForm, is_neutered: v })}>
              <SelectTrigger className="mt-1.5" data-testid="neutered-select">
                <SelectValue placeholder="Seçiniz" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="evet">Kısırlaştırıldı</SelectItem>
                <SelectItem value="hayir">Kısırlaştırılmadı</SelectItem>
                <SelectItem value="bilinmiyor">Bilinmiyor</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Parazit Tedavisi</Label>
            <Select value={dogForm.parasite_treatment || ""} onValueChange={(v) => setDogForm({ ...dogForm, parasite_treatment: v })}>
              <SelectTrigger className="mt-1.5" data-testid="parasite-select">
                <SelectValue placeholder="Seçiniz" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yapildi">Yapıldı</SelectItem>
                <SelectItem value="yapilmadi">Yapılmadı</SelectItem>
                <SelectItem value="bilinmiyor">Bilinmiyor</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Aşı Durumu</Label>
            <Select value={dogForm.vaccination_status || ""} onValueChange={(v) => setDogForm({ ...dogForm, vaccination_status: v })}>
              <SelectTrigger className="mt-1.5" data-testid="vaccination-status-select">
                <SelectValue placeholder="Seçiniz" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tam">Tam</SelectItem>
                <SelectItem value="eksik">Eksik</SelectItem>
                <SelectItem value="yapilmadi">Yapılmadı</SelectItem>
                <SelectItem value="bilinmiyor">Bilinmiyor</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Kilo (kg)</Label>
            <Input
              type="number"
              step="0.1"
              value={dogForm.weight}
              onChange={(e) => setDogForm({ ...dogForm, weight: e.target.value })}
              className="mt-1.5"
              placeholder="Örn: 12.5"
              data-testid="weight-input"
            />
          </div>
        </div>

        {/* Vaccination Details */}
        <div>
          <Label className="flex items-center justify-between">
            <span>Aşı Detayları</span>
            <button
              type="button"
              onClick={() => setDogForm(prev => ({
                ...prev,
                vaccinations: [...(prev.vaccinations || []), { name: "", date: "" }]
              }))}
              className="text-xs text-primary hover:underline flex items-center gap-1"
              data-testid="add-vaccination-btn"
            >
              <Plus className="w-3 h-3" /> Aşı Ekle
            </button>
          </Label>
          {(dogForm.vaccinations || []).length > 0 && (
            <div className="mt-2 space-y-2">
              {dogForm.vaccinations.map((vac, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <Input
                    value={vac.name}
                    onChange={(e) => {
                      const updated = [...dogForm.vaccinations];
                      updated[idx] = { ...updated[idx], name: e.target.value };
                      setDogForm({ ...dogForm, vaccinations: updated });
                    }}
                    placeholder="Aşı adı (ör: Kuduz)"
                    className="flex-1"
                    data-testid={`vaccination-name-${idx}`}
                  />
                  <Input
                    type="date"
                    value={vac.date || ""}
                    onChange={(e) => {
                      const updated = [...dogForm.vaccinations];
                      updated[idx] = { ...updated[idx], date: e.target.value };
                      setDogForm({ ...dogForm, vaccinations: updated });
                    }}
                    className="w-40"
                    data-testid={`vaccination-date-${idx}`}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const updated = dogForm.vaccinations.filter((_, i) => i !== idx);
                      setDogForm({ ...dogForm, vaccinations: updated });
                    }}
                    className="text-destructive hover:text-destructive/80 p-1"
                    data-testid={`remove-vaccination-${idx}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {(dogForm.vaccinations || []).length === 0 && (
            <p className="text-xs text-muted-foreground mt-1">Henüz aşı kaydı eklenmedi</p>
          )}
        </div>

        <div>
          <Label>Sağlık Notları</Label>
          <Textarea
            value={dogForm.health_notes}
            onChange={(e) => setDogForm({ ...dogForm, health_notes: e.target.value })}
            className="mt-1.5"
            placeholder="Kronik hastalıklar, alerjiler, özel ihtiyaçlar..."
            rows={2}
            data-testid="health-notes-input"
          />
        </div>
      </div>

      {/* Vet Info */}
      <div className="p-4 bg-muted/30 rounded-xl space-y-4">
        <Label className="text-base font-medium flex items-center gap-2">
          <Stethoscope className="w-4 h-4" />
          Veteriner Bilgileri
        </Label>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Veteriner Adı</Label>
            <Input
              value={dogForm.vet_name}
              onChange={(e) => setDogForm({ ...dogForm, vet_name: e.target.value })}
              className="mt-1.5"
              placeholder="Vet. Dr. ..."
              data-testid="vet-name-input"
            />
          </div>
          <div>
            <Label>Klinik</Label>
            <Input
              value={dogForm.vet_clinic}
              onChange={(e) => setDogForm({ ...dogForm, vet_clinic: e.target.value })}
              className="mt-1.5"
              placeholder="Klinik adı"
              data-testid="vet-clinic-input"
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl">Can Yönetimi</h1>
          <p className="text-muted-foreground">{dogs.length} can kayıtlı</p>
        </div>
        
        <div className="flex gap-2">
          {/* Bulk Import Button */}
          <Dialog open={showBulkImportDialog} onOpenChange={setShowBulkImportDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" className="btn-pill">
                <Upload className="w-4 h-4 mr-2" />
                Toplu İçe Aktar
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">Toplu Can İçe Aktarma</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <p className="text-muted-foreground text-sm">
                  CSV veya Excel dosyası ile birden fazla canı aynı anda <strong>ekleyebilir</strong> veya mevcut canların bilgilerini <strong>güncelleyebilirsiniz</strong>.
                </p>

                {/* How it works */}
                <div className="p-4 bg-muted/30 rounded-xl space-y-3">
                  <p className="font-medium text-sm">Nasıl Çalışır?</p>
                  <div className="text-xs text-muted-foreground space-y-1.5">
                    <p>&#8226; Sistemde <strong>aynı isimde</strong> bir can varsa bilgileri <strong>güncellenir</strong></p>
                    <p>&#8226; Aynı isimde birden fazla can varsa <strong>mikroçip numarası</strong> ile eşleştirilir</p>
                    <p>&#8226; Eşleşme bulunamazsa <strong>yeni can</strong> oluşturulur</p>
                    <p>&#8226; Boş bırakılan alanlar değiştirilmez (güncelleme modunda)</p>
                  </div>
                </div>

                {/* Column Reference */}
                <div className="p-4 bg-muted/30 rounded-xl space-y-2">
                  <p className="font-medium text-sm">CSV Sütunları</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                    <div><strong>Temel:</strong> isim*, mikrocip, kulak_kupesi, cinsiyet, boyut, irk, durum, kaynak, kaynak_detay, dogum, hikaye</div>
                    <div><strong>Sağlık:</strong> kisirlastirma, asi_durumu, parazit_tedavisi, kilo, saglik_notlari</div>
                    <div><strong>Veteriner:</strong> veteriner_adi, klinik</div>
                    <div><strong>Aşılar:</strong> asi_1_adi, asi_1_tarih, asi_2_adi, asi_2_tarih, ... (10&apos;a kadar)</div>
                  </div>
                  <p className="text-[10px] text-muted-foreground/70 mt-1">* Zorunlu alan. Diğer tüm alanlar opsiyoneldir.</p>
                  <div className="mt-2 pt-2 border-t border-border/50">
                    <p className="text-[11px] text-muted-foreground/90"><strong>Geçerli &quot;durum&quot; değerleri (10):</strong></p>
                    <p className="text-[10px] text-muted-foreground/70 leading-relaxed">
                      barinakta · tedavi_goruyor · sahiplendirmeye_hazir · rezerve · sahiplendirildi · koruyucu_ailede · transfer · iade · kayip · vefat
                    </p>
                    <p className="text-[10px] text-muted-foreground/60 mt-1">
                      💡 Türkçe yazımlar da desteklenir: &quot;Tedavi Görüyor&quot; → <code>tedavi_goruyor</code>, &quot;Alanda / Barınakta&quot; → <code>barinakta</code>, &quot;Geçici Bakımda&quot; → <code>koruyucu_ailede</code>, &quot;Kayıp&quot; → <code>kayip</code>. Bilinmeyen bir değer varsa satır reddedilir.
                    </p>
                  </div>
                </div>

                {/* Example Preview */}
                <div className="p-3 bg-background border border-border rounded-xl overflow-x-auto">
                  <p className="font-medium text-xs mb-2 text-muted-foreground">Örnek CSV:</p>
                  <pre className="text-[10px] text-muted-foreground whitespace-pre leading-relaxed font-mono">{`isim,mikrocip,cinsiyet,boyut,irk,durum,kisirlastirma,asi_durumu,kilo,veteriner_adi,asi_1_adi,asi_1_tarih
Buddy,900108000000001,erkek,orta,Golden Mix,sahiplendirmeye_hazir,evet,tam,12.5,Dr. Yılmaz,Kuduz,2025-06-15
Daisy,,dişi,küçük,Karışık,tedavi_goruyor,hayir,eksik,8.2,,,
Karabaş,,erkek,büyük,Kangal,koruyucu_ailede,evet,tam,32.0,,,`}</pre>
                </div>
                
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" onClick={downloadTemplate} data-testid="download-csv-template">
                    <FileText className="w-4 h-4 mr-2" />
                    CSV Şablonu
                  </Button>
                  <Button variant="outline" onClick={downloadTemplateXlsx} data-testid="download-xlsx-template">
                    <FileText className="w-4 h-4 mr-2" />
                    Excel Şablonu (.xlsx)
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground -mt-1">
                  💡 Türkçe Excel kullanıyorsanız <b>.xlsx şablonu</b> önerilir — Türkçe karakter ve ayırıcı sorunu yaşanmaz.
                </p>
                
                <div className="border-2 border-dashed border-border rounded-xl p-8 text-center">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    accept=".csv,.json,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv,application/json"
                    className="hidden"
                    data-testid="csv-file-input"
                  />
                  <Upload className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
                  <p className="text-muted-foreground mb-3">CSV, Excel veya JSON dosyası yükleyin</p>
                  <Button onClick={() => fileInputRef.current?.click()} disabled={importing} data-testid="select-file-btn">
                    {importing ? "İşleniyor..." : "Dosya Seç"}
                  </Button>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-border/60" />
                  <span className="text-xs text-muted-foreground uppercase tracking-wider">veya</span>
                  <div className="flex-1 h-px bg-border/60" />
                </div>

                <div className="space-y-2 border border-border/60 rounded-xl p-4 bg-muted/20">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Google Sheets Linkinden Getir
                  </label>

                  {/* Hazır şablonu Sheets'te açma yardımcısı — kullanıcı bir tıkla
                      şablon indirir + boş bir Google Sheet açar; File → Import ile
                      şablonu Sheets'e yükleyip doldurabilir. Sonra URL'i altındaki
                      alana yapıştırıp Getir'e basar. Basit, 3 tık akışı. */}
                  <div className="flex flex-col sm:flex-row gap-2 pb-2 border-b border-border/40 mb-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        // 1) İLK: Google Sheets'i yeni sekmede aç. User-click
                        //    gesture'ının ilk async olmayan window.open çağrısı
                        //    olduğu için popup blocker'ı geçer.
                        window.open("https://sheets.new/", "_blank", "noopener,noreferrer");
                        // 2) SONRA: Şablon Excel'ini download link olarak indir.
                        //    Popup değil, indirme olduğu için blocker'a takılmaz.
                        const a = document.createElement("a");
                        a.href = `${API}/admin/dogs/import-template.xlsx`;
                        a.download = "kahu_toplu_import_sablonu.xlsx";
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        toast.success("Google Sheets açıldı + şablon indirildi. Sheets'te File → Import ile şablonu yükle, doldur, linkini buraya yapıştır.", { duration: 8000 });
                      }}
                      className="flex items-center gap-2 text-xs"
                      data-testid="open-sheets-template-btn"
                    >
                      <FileText className="w-3.5 h-3.5" />
                      Google Sheets&apos;te Şablon Aç
                    </Button>
                    <p className="text-[10px] text-muted-foreground/80 leading-tight self-center">
                      Şablon Excel&apos;i indirilir + boş Sheets açılır. <b>File → Import → Upload</b> ile yükle, doldur, aşağıdaki alana Sheet linkini yapıştır.
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <Input
                      value={googleSheetUrl}
                      onChange={(e) => setGoogleSheetUrl(e.target.value)}
                      placeholder="https://docs.google.com/spreadsheets/d/..."
                      disabled={importing}
                      data-testid="google-sheet-url-input"
                    />
                    <Button
                      onClick={handleGoogleSheetFetch}
                      disabled={importing || !googleSheetUrl.trim()}
                      data-testid="google-sheet-fetch-btn"
                    >
                      {importing ? "..." : "Getir"}
                    </Button>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Sheet&apos;te <b>Paylaş → Link sahibi olan herkes → Görüntüleyebilir</b> olmalı. İlk sayfa otomatik okunur; başka sekmeyseniz URL&apos;deki <code>gid=...</code> parametresi kullanılır.
                  </p>
                </div>
                
                {importPreview?.preview && (
                  <div className="p-4 border-2 border-primary/40 bg-primary/5 rounded-xl space-y-3" data-testid="import-preview">
                    <div className="flex items-center justify-between">
                      <p className="font-heading text-base font-semibold">📋 Önizleme — Henüz kaydedilmedi</p>
                      <span className="text-xs text-muted-foreground">Kuru çalışma (dry-run)</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm flex-wrap">
                      <span className="flex items-center gap-1.5 text-green-600 font-medium">
                        <Plus className="w-3.5 h-3.5" /> {importPreview.preview.created_count} yeni can eklenecek
                      </span>
                      <span className="flex items-center gap-1.5 text-blue-600 font-medium">
                        <Edit className="w-3.5 h-3.5" /> {importPreview.preview.updated_count} mevcut güncellenecek
                      </span>
                      {importPreview.preview.error_count > 0 && (
                        <span className="flex items-center gap-1.5 text-destructive font-medium">
                          <AlertCircle className="w-3.5 h-3.5" /> {importPreview.preview.error_count} satır atlanacak
                        </span>
                      )}
                    </div>
                    {importPreview.preview.errors?.length > 0 && (
                      <details className="text-xs">
                        <summary className="cursor-pointer text-destructive font-medium">Atlanacak satırları gör ({importPreview.preview.errors.length})</summary>
                        <div className="mt-2 max-h-40 overflow-y-auto bg-destructive/5 p-3 rounded-lg space-y-1">
                          {importPreview.preview.errors.map((err, i) => (
                            <p key={i} className="text-destructive">Satır {err.row}: {err.error}</p>
                          ))}
                        </div>
                      </details>
                    )}

                    {(importPreview.preview.created?.length > 0 || importPreview.preview.updated?.length > 0) && (() => {
                      const statusLabels = {
                        'barinakta': 'Alanda', 'sahiplendirmeye_hazir': 'Sahiplenmeye Hazır',
                        'sahiplendirildi': 'Sahiplendirildi', 'koruyucu_ailede': 'Geçici Bakımda',
                        'tedavi_goruyor': 'Tedavi Görüyor',
                      };
                      const combined = [
                        ...(importPreview.preview.created || []).map(r => ({ ...r, action: 'new' })),
                        ...(importPreview.preview.updated || []).map(r => ({ ...r, action: 'update' })),
                      ].sort((a, b) => (a.row || 0) - (b.row || 0));
                      const preview10 = combined.slice(0, 10);
                      const remaining = combined.length - preview10.length;
                      return (
                        <div className="text-xs">
                          <p className="font-medium mb-2 text-foreground">İlk {preview10.length} satır önizleme:</p>
                          <div className="overflow-x-auto border border-border/60 rounded-lg">
                            <table className="w-full text-xs" data-testid="import-preview-table">
                              <thead className="bg-muted/50">
                                <tr className="text-left">
                                  <th className="px-2 py-1.5 font-medium">#</th>
                                  <th className="px-2 py-1.5 font-medium">İsim</th>
                                  <th className="px-2 py-1.5 font-medium">Mikroçip</th>
                                  <th className="px-2 py-1.5 font-medium">Durum</th>
                                  <th className="px-2 py-1.5 font-medium">İşlem</th>
                                </tr>
                              </thead>
                              <tbody>
                                {preview10.map((r, i) => (
                                  <tr key={i} className="border-t border-border/40">
                                    <td className="px-2 py-1.5 text-muted-foreground">{r.row ?? '-'}</td>
                                    <td className="px-2 py-1.5 font-medium">{r.name || '-'}</td>
                                    <td className="px-2 py-1.5 font-mono text-[10px]">{r.microchip || '—'}</td>
                                    <td className="px-2 py-1.5">{statusLabels[r.status] || r.status || '-'}</td>
                                    <td className="px-2 py-1.5">
                                      {r.action === 'new' ? (
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-medium">
                                          <Plus className="w-3 h-3" /> Yeni
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium">
                                          <Edit className="w-3 h-3" /> Güncelle
                                        </span>
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {remaining > 0 && (
                            <p className="text-muted-foreground mt-1.5">… ve {remaining} satır daha</p>
                          )}
                        </div>
                      );
                    })()}
                    {importPreview.preview.created_count + importPreview.preview.updated_count === 0 ? (
                      <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-lg">
                        ⚠️ Kaydedilecek hiçbir satır yok. Lütfen dosyayı kontrol edip tekrar yükleyin.
                      </div>
                    ) : (
                      <div className="space-y-2 pt-2">
                        <div className="flex gap-2 flex-wrap">
                          <Button onClick={() => confirmBulkImport(null)} disabled={importing} className="btn-pill flex-1 min-w-[160px]" data-testid="confirm-import-btn">
                            {importing ? "Kaydediliyor..." : `Tümünü Onayla (${importPreview.preview.created_count + importPreview.preview.updated_count})`}
                          </Button>
                        </div>
                        {importPreview.preview.created_count > 0 && importPreview.preview.updated_count > 0 && (
                          <div className="flex gap-2 flex-wrap">
                            <Button variant="outline" onClick={() => confirmBulkImport('create')} disabled={importing} className="flex-1 min-w-[160px]" data-testid="confirm-only-new-btn">
                              Sadece Yeni Ekle ({importPreview.preview.created_count})
                            </Button>
                            <Button variant="outline" onClick={() => confirmBulkImport('update')} disabled={importing} className="flex-1 min-w-[160px]" data-testid="confirm-only-update-btn">
                              Sadece Güncelle ({importPreview.preview.updated_count})
                            </Button>
                          </div>
                        )}
                        <Button variant="ghost" onClick={() => setImportPreview(null)} disabled={importing} size="sm" data-testid="cancel-import-btn" className="w-full text-muted-foreground">
                          İptal
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {importResults && (
                  <div className="p-4 bg-muted/30 rounded-xl space-y-3" data-testid="import-results">
                    <div className="flex items-center gap-4 text-sm">
                      {importResults.created_count > 0 && (
                        <span className="flex items-center gap-1.5 text-green-600 font-medium">
                          <Plus className="w-3.5 h-3.5" /> {importResults.created_count} yeni can
                        </span>
                      )}
                      {importResults.updated_count > 0 && (
                        <span className="flex items-center gap-1.5 text-blue-600 font-medium">
                          <Edit className="w-3.5 h-3.5" /> {importResults.updated_count} güncellendi
                        </span>
                      )}
                      {importResults.error_count > 0 && (
                        <span className="flex items-center gap-1.5 text-destructive font-medium">
                          <AlertCircle className="w-3.5 h-3.5" /> {importResults.error_count} hata
                        </span>
                      )}
                    </div>
                    {importResults.errors?.length > 0 && (
                      <div className="text-xs text-destructive space-y-1 bg-destructive/5 p-3 rounded-lg">
                        {importResults.errors.slice(0, 8).map((err, i) => (
                          <p key={i}>Satır {err.row}: {err.error}</p>
                        ))}
                        {importResults.errors.length > 8 && (
                          <p className="text-muted-foreground">...ve {importResults.errors.length - 8} hata daha</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          {/* Add Dog Button */}
          <Dialog open={showAddDialog} onOpenChange={(open) => { setShowAddDialog(open); if (!open) setDogForm(emptyDog); }}>
            <DialogTrigger asChild>
              <Button className="btn-pill bg-primary text-primary-foreground">
                <Plus className="w-4 h-4 mr-2" />
                Yeni Can Ekle
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto max-w-xl">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">Yeni Can Ekle</DialogTitle>
              </DialogHeader>
              {dogFormFields}
              <div className="flex gap-2 mt-4">
                <Button variant="outline" onClick={() => setShowAddDialog(false)} className="flex-1">
                  İptal
                </Button>
                <Button onClick={handleAddDog} className="flex-1" disabled={!dogForm.name.trim()}>
                  <Plus className="w-4 h-4 mr-2" />
                  Can Ekle
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Status Filter Tabs — bilinen 10 statü + veri anomali sekmesi.
          Rezerve/Transfer/İade nadir statüler, sadece o kategoride can varsa
          görünür. "Diğer" kategorisi denetim boşluğu yarattığı için sistemden
          KALDIRILDI. Anomali sekmesi yalnızca beklenmeyen bir statü tespit
          edilirse (data drift) kırmızı vurgu ile görünür — durum bildirir. */}
      <div className="flex flex-wrap gap-2">
        {[
          { key: "all", label: "Tümü", alwaysShow: true, isAnomaly: false },
          { key: "barinakta", label: "Alanda", alwaysShow: true, isAnomaly: false },
          { key: "tedavi_goruyor", label: "Tedavi", alwaysShow: true, isAnomaly: false },
          { key: "sahiplendirmeye_hazir", label: "Sahiplenmeye Hazır", alwaysShow: true, isAnomaly: false },
          { key: "rezerve", label: "Rezerve", alwaysShow: false, isAnomaly: false },
          { key: "sahiplendirildi", label: "Sahiplendirildi", alwaysShow: true, isAnomaly: false },
          { key: "koruyucu_ailede", label: "Geçici Bakımda", alwaysShow: false, isAnomaly: false },
          { key: "sponsored", label: "Destekçisi Var", alwaysShow: true, isAnomaly: false },
          { key: "transfer", label: "Transfer", alwaysShow: false, isAnomaly: false },
          { key: "iade", label: "İade", alwaysShow: false, isAnomaly: false },
          { key: "kayip", label: "Kayıp", alwaysShow: true, isAnomaly: false },
          { key: "vefat", label: "Vefat", alwaysShow: true, isAnomaly: false },
          { key: "anomaly", label: "⚠ Anomali", alwaysShow: false, isAnomaly: true },
        ]
          .filter(tab => tab.alwaysShow || (statusCounts[tab.key] || 0) > 0)
          .map(tab => (
            <Button
              key={tab.key}
              variant={statusFilter === tab.key ? "default" : "outline"}
              size="sm"
              onClick={() => setStatusFilter(tab.key)}
              className={`rounded-full ${tab.isAnomaly ? "border-red-300 text-red-700 hover:bg-red-50" : ""}`}
              data-testid={`filter-tab-${tab.key}`}
            >
              {tab.label} ({statusCounts[tab.key] || 0})
            </Button>
          ))}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
        <Input
          placeholder="İsim, mikroçip veya ırk ara..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 h-11 rounded-xl"
        />
      </div>

      {/* Dogs Table */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="spinner"></div>
        </div>
      ) : filteredDogs.length === 0 ? (
        <Card className="bg-card border border-border/50">
          <CardContent className="py-20 text-center">
            <Dog className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground">
              {search || statusFilter !== "all" ? "Sonuç bulunamadı" : "Henüz can eklenmemiş"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-card border border-border/50">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border/50 bg-muted/30">
                    <th className="text-left p-4 font-medium">Can</th>
                    <th className="text-left p-4 font-medium">Mikroçip ID</th>
                    <th className="text-left p-4 font-medium">Durum</th>
                    <th className="text-left p-4 font-medium">Detaylar</th>
                    <th className="text-left p-4 font-medium">Geliş</th>
                    <th className="text-right p-4 font-medium">İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDogs.map((dog) => (
                    <tr key={dog.dog_id} className="border-b border-border/50 hover:bg-muted/20">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          {dog.photo_url ? (
                            <img src={dog.photo_url} alt={dog.name} className="w-12 h-12 rounded-lg object-cover" />
                          ) : (
                            <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center">
                              <PawPrint className="w-6 h-6 text-muted-foreground" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium">{dog.name}</p>
                            <p className="text-xs text-muted-foreground">{dog.breed || "Karisik"}</p>
                            {updateReminders[dog.dog_id] !== undefined && (
                              <span className="inline-flex items-center gap-1 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full mt-0.5" data-testid={`update-reminder-${dog.dog_id}`}>
                                <Clock className="w-3 h-3" />
                                Güncelleme zamanı {updateReminders[dog.dog_id] !== null ? `(${updateReminders[dog.dog_id]} gün)` : ""}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <code className="text-xs bg-muted px-2 py-1 rounded font-mono">
                          {dog.microchip_number || "—"}
                        </code>
                      </td>
                      <td className="p-4">
                        <div className="flex flex-col gap-1.5 items-start">
                          {getStatusBadge(dog.status)}
                          {(dog.guardian_active_count || 0) > 0 && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-200"
                              title={`${dog.guardian_active_count} aktif Koruyucu Aile destekçisi`}
                              data-testid={`guardian-badge-${dog.dog_id}`}
                            >
                              <Heart className="w-2.5 h-2.5" strokeWidth={2} />
                              {dog.guardian_active_count} destekçi
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        <span>{dog.gender === "male" ? "♂ Erkek" : dog.gender === "female" ? "♀ Dişi" : "?"}</span>
                        <span className="mx-1">·</span>
                        <span>{dog.size === "small" ? "Küçük" : dog.size === "medium" ? "Orta" : dog.size === "large" ? "Büyük" : "Dev"}</span>
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {dog.intake_date ? new Date(dog.intake_date).toLocaleDateString("tr-TR") : "—"}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-1">
                          {/* Quick Status Update */}
                          <Select
                            value={dog.status}
                            onValueChange={(v) => handleStatusChange(dog.dog_id, v)}
                          >
                            <SelectTrigger className="w-8 h-8 p-0 border-0 bg-transparent">
                              <ChevronRight className="w-4 h-4" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="barinakta">→ Alanda</SelectItem>
                              <SelectItem value="tedavi_goruyor">→ Tedavi Görüyor</SelectItem>
                              <SelectItem value="sahiplendirmeye_hazir">→ Sahiplenmeye Hazır</SelectItem>
                              <SelectItem value="rezerve">→ Rezerve</SelectItem>
                              <SelectItem value="sahiplendirildi">→ Sahiplendirildi</SelectItem>
                              <SelectItem value="koruyucu_ailede">→ Geçici Bakımda</SelectItem>
                              <SelectItem value="transfer">→ Transfer</SelectItem>
                              <SelectItem value="iade">→ İade</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button variant="ghost" size="sm" title="Onizleme" onClick={() => openPreview(dog)}>
                              <Eye className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openEditDialog(dog)} title="Düzenle">
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => openDeleteDialog(dog)} title="Sil" className="text-destructive hover:text-destructive">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={(open) => { setShowEditDialog(open); if (!open) { setSelectedDog(null); setDogForm(emptyDog); } }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto max-w-xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {selectedDog?.name} - Düzenle
            </DialogTitle>
          </DialogHeader>
          {dogFormFields}
          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowEditDialog(false)} className="flex-1">
              İptal
            </Button>
            <Button onClick={handleUpdateDog} className="flex-1">
              <CheckCircle className="w-4 h-4 mr-2" />
              Kaydet
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl text-destructive">
              Canı Sil
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-muted-foreground">
              <strong>{selectedDog?.name}</strong> adlı canı silmek istediğinizden emin misiniz? 
              Bu işlem geri alınamaz ve tüm ilişkili veriler (sağlık kayıtları, yorumlar, vb.) silinecektir.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} className="flex-1">
              İptal
            </Button>
            <Button variant="destructive" onClick={handleDeleteDog} className="flex-1">
              <Trash2 className="w-4 h-4 mr-2" />
              Evet, Sil
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewDog} onOpenChange={(open) => { if (!open) { setPreviewDog(null); setPreviewPhotos([]); } }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto max-w-lg" data-testid="dog-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">{previewDog?.name}</DialogTitle>
          </DialogHeader>
          {previewDog && (
            <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
              {/* Photo */}
              {(previewDog.photo_url || previewPhotos.length > 0) && (
                <div>
                  <img
                    src={previewDog.photo_url || previewPhotos[0]?.media_base64 || (previewPhotos[0]?.image_base64 ? `data:image/jpeg;base64,${previewPhotos[0].image_base64}` : "")}
                    alt={previewDog.name}
                    className="w-full max-h-64 object-contain rounded-xl"
                    style={{ background: "linear-gradient(135deg, #f5f0eb 0%, #ede6df 100%)" }}
                  />
                  {previewPhotos.length > 1 && (
                    <div className="flex gap-2 mt-2 overflow-x-auto">
                      {previewPhotos.slice(1).map((p, i) => (
                        <img key={i} src={p.media_base64 || (p.image_base64 ? `data:image/jpeg;base64,${p.image_base64}` : "")} alt="" className="w-16 h-16 object-cover rounded-lg shrink-0" />
                      ))}
                    </div>
                  )}
                </div>
              )}
              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="p-3 bg-muted/30 rounded-xl">
                  <p className="text-muted-foreground text-xs">Durum</p>
                  <p className="font-medium mt-0.5">{
                    ({ barinakta: "Alanda", sahiplendirmeye_hazir: "Sahiplenmeye Hazir", sahiplendirildi: "Sahiplendirildi", koruyucu_ailede: "Geçici Bakımda", tedavi_altinda: "Tedavi Altinda", vefat_etti: "Vefat Etti" })[previewDog.status] || previewDog.status
                  }</p>
                </div>
                <div className="p-3 bg-muted/30 rounded-xl">
                  <p className="text-muted-foreground text-xs">Cinsiyet / Boy</p>
                  <p className="font-medium mt-0.5">{previewDog.gender === "male" ? "Erkek" : "Disi"} / {previewDog.size === "small" ? "Kucuk" : previewDog.size === "medium" ? "Orta" : previewDog.size === "large" ? "Buyuk" : "Dev"}</p>
                </div>
                {previewDog.breed && (
                  <div className="p-3 bg-muted/30 rounded-xl">
                    <p className="text-muted-foreground text-xs">Irk</p>
                    <p className="font-medium mt-0.5">{previewDog.breed}</p>
                  </div>
                )}
                {previewDog.microchip_number && (
                  <div className="p-3 bg-muted/30 rounded-xl">
                    <p className="text-muted-foreground text-xs">Mikrocip</p>
                    <p className="font-medium mt-0.5 font-mono text-xs">{previewDog.microchip_number}</p>
                  </div>
                )}
              </div>
              {/* Story */}
              {previewDog.story_short && (
                <div className="p-3 bg-muted/30 rounded-xl">
                  <p className="text-muted-foreground text-xs mb-1">Hikaye</p>
                  <p className="text-sm">{previewDog.story_short}</p>
                </div>
              )}
              {/* Compatibility */}
              <div className="flex flex-wrap gap-2">
                {previewDog.is_child_friendly && <Badge variant="outline" className="text-xs">Cocuk Dostu</Badge>}
                {previewDog.is_dog_friendly && <Badge variant="outline" className="text-xs">Kopek Dostu</Badge>}
                {previewDog.is_cat_friendly && <Badge variant="outline" className="text-xs">Kedi Dostu</Badge>}
                {previewDog.is_apartment_suitable && <Badge variant="outline" className="text-xs">Apartman Uygun</Badge>}
              </div>
              {/* Actions */}
              <div className="flex gap-2 pt-2 border-t border-border/50">
                <Link to={`/can/${previewDog.dog_id}`} className="flex-1">
                  <Button variant="outline" className="w-full text-sm">
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> Profil Sayfasi
                  </Button>
                </Link>
                <Button className="flex-1 text-sm" onClick={() => { setPreviewDog(null); openEditDialog(previewDog); }}>
                  <Edit className="w-3.5 h-3.5 mr-1.5" /> Duzenle
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
// Guardian Hub — Koruyucu Aileler + Bekleyen Kayıtlar + Fonzip bağlantısı tek yerde
function GuardianHub({ defaultTab = "families" }) {
  const [tab, setTab] = useState(defaultTab);
  const [counts, setCounts] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    axios.get(`${API}/guardians?status=active`, { withCredentials: true })
      .then((r) => setCounts(r.data.counts || {}))
      .catch(() => {});
  }, [tab, refreshKey]);

  const pending = (counts.pending_fonzip || 0) + (counts.pending_payment || 0);

  return (
    <div className="space-y-6" data-testid="guardian-hub">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl">Koruyucu Aile / Düzenli Bağış</h1>
        <p className="text-muted-foreground">
          Düzenli destekçiler Fonzip'te tahsil edilir; ödeme onaylandığında kayıt otomatik olarak Koruyucu Aile'ye dönüşür.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full h-auto bg-muted/50 p-1 rounded-xl flex flex-wrap justify-start gap-1 md:grid md:grid-cols-3 md:gap-0 md:flex-nowrap">
          <TabsTrigger value="families" className="rounded-lg shrink-0 whitespace-nowrap" data-testid="guardian-tab-families">
            Koruyucu Aileler {counts.active ? <Badge variant="secondary" className="ml-2">{counts.active}</Badge> : null}
          </TabsTrigger>
          <TabsTrigger value="pending" className="rounded-lg shrink-0 whitespace-nowrap" data-testid="guardian-tab-pending">
            Bekleyen Kayıtlar {pending ? <Badge variant="secondary" className="ml-2">{pending}</Badge> : null}
          </TabsTrigger>
          <TabsTrigger value="fonzip" className="rounded-lg shrink-0 whitespace-nowrap" data-testid="guardian-tab-fonzip">
            Fonzip Bağlantısı
          </TabsTrigger>
        </TabsList>

        <TabsContent value="families" className="mt-6"><GuardiansManagement key={`fam-${refreshKey}`} /></TabsContent>
        <TabsContent value="pending" className="mt-6"><GuardianIntentsPanel onChanged={() => setRefreshKey((k) => k + 1)} /></TabsContent>
        <TabsContent value="fonzip" className="mt-6"><FonzipPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

// Guardians Management Component
function GuardiansManagement() {
  const [guardians, setGuardians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [guardianLinks, setGuardianLinks] = useState({});
  const [expandedGuardian, setExpandedGuardian] = useState(null);

  useEffect(() => {
    fetchGuardians();
  }, []);

  const fetchGuardians = async () => {
    try {
      const response = await axios.get(`${API}/guardians?status=active`, { withCredentials: true });
      setGuardians(response.data.guardians);
    } catch (error) {
      console.error("Guardians fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const copyWhatsAppMessage = async (guardian) => {
    let link = guardianLinks[guardian.dog_id];
    if (!link) {
      try {
        const res = await axios.get(`${API}/admin/dogs/${guardian.dog_id}/guardian-link`, { withCredentials: true });
        link = res.data.guardian_link;
        setGuardianLinks(prev => ({ ...prev, [guardian.dog_id]: link }));
      } catch {
        toast.error("Link alınamadı");
        return;
      }
    }
    const msg = `Merhaba! ${guardian.dog_name || "Can dostunuz"} için son güncellemeler burada:\n${link}\nSevgiyle.`;
    navigator.clipboard.writeText(msg)
      .then(() => toast.success("WhatsApp mesajı kopyalandı!"))
      .catch(() => toast.error("Kopyalanamadı — mesajı elle seçin"));
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-xl">Aktif Koruyucu Aileler</h2>
        <p className="text-sm text-muted-foreground">{guardians.length} aktif koruyucu aile · ödemesi onaylanmış düzenli destekçiler</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="spinner"></div>
        </div>
      ) : guardians.length === 0 ? (
        <Card className="bg-card border border-border/50">
          <CardContent className="py-20 text-center">
            <Heart className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
            <p className="text-muted-foreground">Henüz koruyucu aile yok</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {guardians.map((guardian) => (
            <Card key={guardian.guardian_id} className="bg-card border border-border/50">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="font-medium">{guardian.guardian_name}</p>
                    <p className="text-sm text-muted-foreground">{guardian.guardian_email}</p>
                  </div>
                  <Badge className="status-foster">{guardian.monthly_amount} TL/ay</Badge>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                  <Dog className="w-4 h-4" />
                  <span>{guardian.dog_name || "Bilinmeyen"}</span>
                  {guardian.guardian_phone && (
                    <>
                      <span className="text-border">|</span>
                      <span>{guardian.guardian_phone}</span>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <p className="text-xs text-muted-foreground flex-1">
                    Başlangıç: {new Date(guardian.start_date).toLocaleDateString("tr-TR")}
                  </p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setExpandedGuardian(expandedGuardian === guardian.guardian_id ? null : guardian.guardian_id)}
                    data-testid={`guardian-updates-toggle-${guardian.guardian_id}`}
                  >
                    <Camera className="w-3.5 h-3.5 mr-1" />
                    Güncelleme
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-green-700 border-green-200 hover:bg-green-50"
                    onClick={() => copyWhatsAppMessage(guardian)}
                    data-testid={`guardian-whatsapp-${guardian.guardian_id}`}
                  >
                    <MessageCircle className="w-3.5 h-3.5 mr-1" />
                    WhatsApp
                  </Button>
                </div>

                {/* Inline DogUpdatesPanel */}
                {expandedGuardian === guardian.guardian_id && (
                  <div className="mt-4 pt-4 border-t border-border/50">
                    <DogUpdatesPanel dogId={guardian.dog_id} dogName={guardian.dog_name || "Can"} />
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// Donations Management Component
// Users Management Component (Admin Only)
function UsersManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data.users);
    } catch (error) {
      console.error("Users fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateRole = async (userId, role) => {
    try {
      await axios.put(`${API}/users/${userId}/role?role=${role}`);
      toast.success("Rol güncellendi");
      fetchUsers();
    } catch (error) {
      toast.error("Güncelleme başarısız");
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: "Yönetici",
      shelter_staff: "Barınak Personeli",
      volunteer: "Gönüllü",
      sponsor: "Sponsor",
      adopter: "Sahiplendirici",
      municipality: "Belediye",
      foreign_partner: "Yurt Dışı Partner"
    };
    return labels[role] || role;
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl">Kullanıcılar</h1>
        <p className="text-muted-foreground">{users.length} kullanıcı</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="spinner"></div>
        </div>
      ) : (
        <Card className="bg-card border border-border/50">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border/50">
                    <th className="text-left p-4 font-medium">Kullanıcı</th>
                    <th className="text-left p-4 font-medium">E-posta</th>
                    <th className="text-left p-4 font-medium">Rol</th>
                    <th className="text-left p-4 font-medium">Kayıt</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.user_id} className="border-b border-border/50">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={u.picture || `https://ui-avatars.com/api/?name=${u.name}&background=random`}
                            alt={u.name}
                            className="w-8 h-8 rounded-full"
                          />
                          <p className="font-medium">{u.name}</p>
                        </div>
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">{u.email}</td>
                      <td className="p-4">
                        <Select value={u.role} onValueChange={(v) => updateRole(u.user_id, v)}>
                          <SelectTrigger className="w-40">
                            <SelectValue>{getRoleLabel(u.role)}</SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="admin">Yönetici</SelectItem>
                            <SelectItem value="shelter_staff">Barınak Personeli</SelectItem>
                            <SelectItem value="volunteer">Gönüllü</SelectItem>
                            <SelectItem value="sponsor">Sponsor</SelectItem>
                            <SelectItem value="adopter">Sahiplendirici</SelectItem>
                            <SelectItem value="municipality">Belediye</SelectItem>
                            <SelectItem value="foreign_partner">Yurt Dışı Partner</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-4 text-sm text-muted-foreground">
                        {new Date(u.created_at).toLocaleDateString("tr-TR")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Municipality Panel Component
function MunicipalityPanel() {
  const [stats, setStats] = useState(null);
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const now = new Date();
  const [exportName, setExportName] = useState("Etimesgut Belediyesi");
  const [exportMonth, setExportMonth] = useState(String(now.getMonth() + 1));
  const [exportYear, setExportYear] = useState(String(now.getFullYear()));

  const monthNames = [
    "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
    "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"
  ];
  const yearOptions = Array.from({ length: 5 }, (_, i) => String(now.getFullYear() - i));

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, trendsRes] = await Promise.all([
        axios.get(`${API}/municipality/stats`),
        axios.get(`${API}/municipality/trends`)
      ]);
      setStats(statsRes.data);
      setTrends(trendsRes.data.trends || []);
    } catch (error) {
      console.error("Municipality data error:", error);
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!exportName.trim()) {
      toast.error("Lütfen belediye adını girin");
      return;
    }
    setExporting(true);
    try {
      const params = new URLSearchParams({
        municipality_name: exportName.trim(),
        period_month: exportMonth,
        period_year: exportYear,
      });
      const response = await axios.get(`${API}/municipality/export-pdf?${params.toString()}`, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeName = exportName.trim().replace(/[^A-Za-z0-9_-]+/g, "_");
      a.download = `kahu-${safeName}-${monthNames[parseInt(exportMonth, 10) - 1]}_${exportYear}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF rapor indirildi");
      setExportOpen(false);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Rapor indirilemedi");
    } finally {
      setExporting(false);
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      barinakta: "Alanda",
      sahiplendirmeye_hazir: "Sahiplenmeye Hazır",
      sahiplendirildi: "Sahiplendirildi",
      koruyucu_ailede: "Geçici Bakımda",
      tedavi_goruyor: "Tedavi Görüyor",
      rezerve: "Rezerve",
      transfer: "Transfer",
      kayip: "Kayıp",
      vefat: "Vefat",
      iade: "İade",
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl">Barınak Panorama</h1>
          <p className="text-muted-foreground">STK/Barınak için operasyonel içgörü — Pativista MVP</p>
        </div>
        <Button onClick={() => setExportOpen(true)} disabled={exporting} className="btn-pill" data-testid="export-report-btn">
          {exporting ? "İndiriliyor..." : "PDF Rapor İndir"}
        </Button>
      </div>

      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto max-w-md" data-testid="export-report-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">KAHU Özet Raporu Oluştur</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="muni-name">Rapor Kime Hitaben? (Alıcı / Kurum Adı)</Label>
              <Input
                id="muni-name"
                value={exportName}
                onChange={(e) => setExportName(e.target.value)}
                placeholder="Örn. Etimesgut Belediyesi, ABC Sponsoru, STK Adı…"
                data-testid="export-municipality-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Ay</Label>
                <Select value={exportMonth} onValueChange={setExportMonth}>
                  <SelectTrigger data-testid="export-period-month"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {monthNames.map((m, idx) => (
                      <SelectItem key={idx} value={String(idx + 1)}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Yıl</Label>
                <Select value={exportYear} onValueChange={setExportYear}>
                  <SelectTrigger data-testid="export-period-year"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {yearOptions.map((y) => (
                      <SelectItem key={y} value={y}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Rapor; genel bakış, dönem performansı, durum dağılımı ve son 6 aylık eğilimi içerir (1-2 sayfa, kurumsal sade tasarım).
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setExportOpen(false)} data-testid="export-cancel-btn">İptal</Button>
              <Button onClick={handleExport} disabled={exporting} className="btn-pill" data-testid="export-confirm-btn">
                {exporting ? "Hazırlanıyor..." : "PDF İndir"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Overview Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card className="bg-card border border-border/50">
          <CardContent className="p-5">
            <p className="text-sm text-muted-foreground">Toplam Can</p>
            <p className="font-heading text-3xl text-primary">{stats?.overview?.total_dogs || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border border-border/50">
          <CardContent className="p-5">
            <p className="text-sm text-muted-foreground">Sahiplendirildi</p>
            <p className="font-heading text-3xl text-accent">{stats?.overview?.total_adopted || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border border-border/50">
          <CardContent className="p-5">
            <p className="text-sm text-muted-foreground">Koruyucu Aile</p>
            <p className="font-heading text-3xl text-secondary">{stats?.overview?.active_guardians || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border border-border/50">
          <CardContent className="p-5">
            <p className="text-sm text-muted-foreground">Bekleyen Başvuru</p>
            <p className="font-heading text-3xl">{stats?.overview?.pending_applications || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border border-border/50">
          <CardContent className="p-5">
            <p className="text-sm text-muted-foreground">Toplam Kullanıcı</p>
            <p className="font-heading text-3xl">{stats?.overview?.total_users || 0}</p>
          </CardContent>
        </Card>
      </div>

      {/* Financial Metrics */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="bg-card border border-border/50">
          <CardHeader>
            <CardTitle className="font-heading text-xl">Finansal Özet</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center p-4 bg-muted/30 rounded-xl">
                <span className="text-muted-foreground">Toplam Bağış</span>
                <span className="font-heading text-2xl text-primary">
                  {(stats?.financial_metrics?.total_donations || 0).toLocaleString('tr-TR')}₺
                </span>
              </div>
              <div className="flex justify-between items-center p-4 bg-muted/30 rounded-xl">
                <span className="text-muted-foreground">Aylık Koruyucu Aile Geliri</span>
                <span className="font-heading text-2xl text-secondary">
                  {(stats?.financial_metrics?.monthly_guardian_revenue || 0).toLocaleString('tr-TR')}₺
                </span>
              </div>
              {stats?.financial_metrics?.donations_by_type && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-4">
                  {Object.entries(stats.financial_metrics.donations_by_type)
                    .sort(([, a], [, b]) => (b.total || 0) - (a.total || 0))
                    .map(([label, data]) => (
                      <div key={label} className="p-3 bg-muted/20 rounded-lg text-center">
                        <p className="text-xs text-muted-foreground">{label}</p>
                        <p className="font-medium">{(data.total || 0).toLocaleString('tr-TR')}₺</p>
                        <p className="text-xs text-muted-foreground">{data.count || 0} adet</p>
                      </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border border-border/50">
          <CardHeader>
            <CardTitle className="font-heading text-xl">Sahiplendirme Metrikleri</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-accent/10 rounded-xl text-center">
                  <p className="text-sm text-muted-foreground">Toplam Sahiplendirilen</p>
                  <p className="font-heading text-3xl text-accent">{stats?.adoption_metrics?.total_adopted || 0}</p>
                </div>
                <div className="p-4 bg-primary/10 rounded-xl text-center" title="Karara bağlanan başvurulardan onaylananların yüzdesi. (Onay + Ret toplamına göre hesaplanır)">
                  <p className="text-sm text-muted-foreground">Dönüşüm Oranı</p>
                  <p className="font-heading text-3xl text-primary">%{stats?.adoption_metrics?.conversion_rate || 0}</p>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {stats?.adoption_metrics?.approved_applications || 0} onay / {(stats?.adoption_metrics?.approved_applications || 0) + (stats?.adoption_metrics?.rejected_applications || 0)} karar
                  </p>
                </div>
              </div>
              <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                <span className="text-sm">Bekleyen Başvurular</span>
                <Badge>{stats?.adoption_metrics?.pending_applications || 0}</Badge>
              </div>
              <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
                <span className="text-sm">Onaylanan Başvurular</span>
                <Badge variant="secondary">{stats?.adoption_metrics?.approved_applications || 0}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bu Ay Özet — STK operasyonu için mini KPI şeridi */}
      <Card className="bg-card border border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-xl flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" strokeWidth={1.5} />
            Bu Ay Özet
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            {new Date().toLocaleDateString("tr-TR", { month: "long", year: "numeric" })} — sıcak veri
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="p-4 bg-primary/5 rounded-xl">
              <p className="text-xs text-muted-foreground">Yeni Kayıt</p>
              <p className="font-heading text-2xl text-primary mt-0.5">
                {stats?.shelter_focus?.monthly_summary?.new_dogs || 0}
              </p>
              <p className="text-[10px] text-muted-foreground">can bu ay eklendi</p>
            </div>
            <div className="p-4 bg-accent/5 rounded-xl">
              <p className="text-xs text-muted-foreground">Sahiplendirme</p>
              <p className="font-heading text-2xl text-accent mt-0.5">
                {stats?.shelter_focus?.monthly_summary?.adoptions || 0}
              </p>
              <p className="text-[10px] text-muted-foreground">yuvaya kavuştu</p>
            </div>
            <div className="p-4 bg-secondary/5 rounded-xl">
              <p className="text-xs text-muted-foreground">Bağış Toplamı</p>
              <p className="font-heading text-2xl text-secondary mt-0.5">
                {(stats?.shelter_focus?.monthly_summary?.donation_amount || 0).toLocaleString('tr-TR')}₺
              </p>
              <p className="text-[10px] text-muted-foreground">
                {stats?.shelter_focus?.monthly_summary?.donation_count || 0} işlem
              </p>
            </div>
            <div className="p-4 bg-rose-500/5 rounded-xl">
              <p className="text-xs text-muted-foreground">Yeni Koruyucu</p>
              <p className="font-heading text-2xl text-rose-600 mt-0.5">
                {stats?.shelter_focus?.monthly_summary?.new_guardians || 0}
              </p>
              <p className="text-[10px] text-muted-foreground">aile aramıza katıldı</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Aksiyon Bekleyen + Sağlık Takibi (yan yana) */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* AKSIYON BEKLEYEN */}
        <Card className="bg-card border border-amber-200/50 shadow-amber-100/20">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600" strokeWidth={1.5} />
              Aksiyon Bekleyen Canlar
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Koruyucusuz kalanlar ve 60+ gündür alanda olanlar
            </p>
          </CardHeader>
          <CardContent>
            {/* Özet çubuğu */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="p-3 bg-amber-50 border border-amber-100 rounded-lg text-center">
                <p className="text-xs text-amber-700/80">Koruyucusuz</p>
                <p className="font-heading text-2xl text-amber-700">
                  {stats?.shelter_focus?.action_needed?.no_guardian_count || 0}
                  <span className="text-sm text-muted-foreground font-normal">
                    /{stats?.shelter_focus?.action_needed?.no_guardian_total_care || 0}
                  </span>
                </p>
              </div>
              <div className="p-3 bg-orange-50 border border-orange-100 rounded-lg text-center">
                <p className="text-xs text-orange-700/80">60+ Gün Bakımda</p>
                <p className="font-heading text-2xl text-orange-700">
                  {stats?.shelter_focus?.action_needed?.long_stay_count || 0}
                </p>
              </div>
            </div>

            {/* Koruyucusuz canların listesi (max 5) — sadece bilgi, link yok
                (admin panel içindeyken /can/{id} public'e atılmasın) */}
            {stats?.shelter_focus?.action_needed?.no_guardian_dogs?.length > 0 && (
              <>
                <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2 font-medium">
                  Koruyucu bekleyenler
                </p>
                <div className="space-y-1">
                  {stats.shelter_focus.action_needed.no_guardian_dogs.map((d) => (
                    <div
                      key={d.dog_id}
                      className="flex items-center justify-between p-2 rounded-lg bg-muted/20"
                      data-testid={`action-no-guardian-${d.dog_id}`}
                    >
                      <span className="text-sm font-medium">{d.name}</span>
                      <Badge variant="outline" className="text-[10px] border-amber-200 text-amber-700 bg-amber-50">
                        0 koruyucu
                      </Badge>
                    </div>
                  ))}
                </div>
                {stats.shelter_focus.action_needed.no_guardian_count > 5 && (
                  <p className="text-[11px] text-muted-foreground mt-2 text-center">
                    + {stats.shelter_focus.action_needed.no_guardian_count - 5} can daha
                  </p>
                )}
              </>
            )}
            {stats?.shelter_focus?.action_needed?.no_guardian_count === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                🎉 Bakımdaki tüm canların bir koruyucusu var.
              </p>
            )}
          </CardContent>
        </Card>

        {/* SAĞLIK TAKİBİ */}
        <Card className="bg-card border border-border/50">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Heart className="w-5 h-5 text-rose-600" strokeWidth={1.5} />
              Sağlık Takibi
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              Aşı eksikleri ve bekleyen kısırlaştırma — bakımdaki canlar
            </p>
          </CardHeader>
          <CardContent>
            {stats?.shelter_focus?.health_followups?.has_data ? (
              <>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="p-3 bg-rose-50 border border-rose-100 rounded-lg text-center">
                    <p className="text-xs text-rose-700/80">Aşısı Eksik</p>
                    <p className="font-heading text-2xl text-rose-700">
                      {stats.shelter_focus.health_followups.vaccination_missing || 0}
                    </p>
                  </div>
                  <div className="p-3 bg-purple-50 border border-purple-100 rounded-lg text-center">
                    <p className="text-xs text-purple-700/80">Kısırlaştırma Bekleyen</p>
                    <p className="font-heading text-2xl text-purple-700">
                      {stats.shelter_focus.health_followups.not_neutered || 0}
                    </p>
                  </div>
                </div>
                {stats.shelter_focus.health_followups.missing_vax_dogs?.length > 0 && (
                  <>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2 font-medium">
                      Aşısı eksik canlar
                    </p>
                    <div className="space-y-1">
                      {stats.shelter_focus.health_followups.missing_vax_dogs.map((d) => (
                        <div
                          key={d.dog_id}
                          className="flex items-center justify-between p-2 rounded-lg bg-muted/20"
                        >
                          <span className="text-sm font-medium">{d.name}</span>
                          <Badge variant="outline" className="text-[10px] border-rose-200 text-rose-700 bg-rose-50">
                            aşı eksik
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {stats.shelter_focus.health_followups.vaccination_missing === 0 &&
                  stats.shelter_focus.health_followups.not_neutered > 0 && (
                    <p className="text-xs text-muted-foreground text-center py-2">
                      Aşı kaydı eksik can yok — sıradaki iş kısırlaştırma planlaması.
                    </p>
                  )}
                {((stats.shelter_focus.health_followups.vaccination_unknown || 0) > 0 ||
                  (stats.shelter_focus.health_followups.neuter_unknown || 0) > 0) && (
                  <p
                    className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-2 mt-2"
                    data-testid="health-unknown-note"
                  >
                    Bilgisi girilmemiş:{" "}
                    {(stats.shelter_focus.health_followups.vaccination_unknown || 0) > 0 &&
                      `${stats.shelter_focus.health_followups.vaccination_unknown} canın aşı kaydı`}
                    {(stats.shelter_focus.health_followups.vaccination_unknown || 0) > 0 &&
                      (stats.shelter_focus.health_followups.neuter_unknown || 0) > 0 && ", "}
                    {(stats.shelter_focus.health_followups.neuter_unknown || 0) > 0 &&
                      `${stats.shelter_focus.health_followups.neuter_unknown} canın kısırlık bilgisi`}
                    . Bu canlar yukarıdaki sayılara dahil değil.
                  </p>
                )}
              </>
            ) : (
              <div className="py-6 text-center">
                <Heart className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" strokeWidth={1.2} />
                <p className="text-sm text-muted-foreground">
                  Bakımdaki tüm canların aşı ve kısırlaştırma kaydı tamam.
                </p>
                <p className="text-xs text-muted-foreground/70 mt-1">
                  Yeni can eklendiğinde ve sağlık statüsü işaretlendiğinde burada takip görürsün.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Durum Dağılımı — full-width, sade */}
      <Card className="bg-card border border-border/50">
        <CardHeader>
          <CardTitle className="font-heading text-lg">Durum Dağılımı</CardTitle>
        </CardHeader>
        <CardContent>
          {stats?.dogs_by_status && (
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
              {Object.entries(stats.dogs_by_status).map(([status, count]) => (
                <div key={status} className="flex items-center justify-between">
                  <span className="text-sm">{getStatusLabel(status)}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-32 h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full"
                        style={{ width: `${(count / (stats.overview?.total_dogs || 1)) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium w-8">{count}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Trends Chart */}
      <Card className="bg-card border border-border/50">
        <CardHeader>
          <CardTitle className="font-heading text-xl">12 Aylık Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={trends}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month_label" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <Tooltip 
                contentStyle={{ 
                  background: "hsl(var(--card))", 
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "8px"
                }} 
              />
              <Line type="monotone" dataKey="new_dogs" stroke="hsl(var(--primary))" strokeWidth={2} name="Yeni Canlar" dot={{ fill: "hsl(var(--primary))" }} />
              <Line type="monotone" dataKey="adoptions" stroke="hsl(var(--accent))" strokeWidth={2} name="Sahiplendirme" dot={{ fill: "hsl(var(--accent))" }} />
              <Line type="monotone" dataKey="new_guardians" stroke="hsl(var(--secondary))" strokeWidth={2} name="Yeni Koruyucu" dot={{ fill: "hsl(var(--secondary))" }} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Engagement */}
      <Card className="bg-card border border-border/50">
        <CardHeader>
          <CardTitle className="font-heading text-xl">Kullanıcı Etkileşimi</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="p-4 bg-muted/30 rounded-xl text-center">
              <p className="text-sm text-muted-foreground">Son 30 Gün Etkinlik</p>
              <p className="font-heading text-3xl">{stats?.engagement?.events_last_30_days || 0}</p>
            </div>
            {stats?.engagement?.users_by_role && Object.entries(stats.engagement.users_by_role).slice(0, 2).map(([role, count]) => {
              const roleLabels = { admin: "Yönetici", shelter_staff: "Personel", volunteer: "Gönüllü", sponsor: "Sponsor", adopter: "Sahiplendirici" };
              return (
                <div key={role} className="p-4 bg-muted/30 rounded-xl text-center">
                  <p className="text-sm text-muted-foreground">{roleLabels[role] || role}</p>
                  <p className="font-heading text-3xl">{count}</p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Transparency Management Component (Yaşam Kanıtı)
function TransparencyManagement() {
  const [dogs, setDogs] = useState([]);
  const [lossRecords, setLossRecords] = useState([]);
  const [dailyCounts, setDailyCounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDailyCountDialog, setShowDailyCountDialog] = useState(false);
  const [showLossDialog, setShowLossDialog] = useState(false);
  const [selectedDog, setSelectedDog] = useState(null);
  // Detay modalı için seçili kayıt
  const [selectedLossRecord, setSelectedLossRecord] = useState(null);
  // Edit modu state
  const [isEditingLoss, setIsEditingLoss] = useState(false);
  const [editLossForm, setEditLossForm] = useState(null);
  const [savingLossEdit, setSavingLossEdit] = useState(false);

  // Edit modu başlat — mevcut kaydın alanlarını forma yükle
  const startEditLoss = () => {
    if (!selectedLossRecord) return;
    setEditLossForm({
      loss_type: selectedLossRecord.loss_type || "vefat",
      loss_reason: selectedLossRecord.loss_reason || "bilinmiyor",
      loss_date: (selectedLossRecord.loss_date || "").slice(0, 10),
      story: selectedLossRecord.story || "",
      last_seen_location: selectedLossRecord.last_seen_location || "",
      last_seen_date: (selectedLossRecord.last_seen_date || "").slice(0, 10),
      additional_notes: selectedLossRecord.additional_notes || "",
    });
    setIsEditingLoss(true);
  };

  const cancelEditLoss = () => {
    setIsEditingLoss(false);
    setEditLossForm(null);
  };

  const saveLossEdit = async () => {
    if (!selectedLossRecord || !editLossForm) return;
    if (!editLossForm.story || editLossForm.story.trim().length < 20) {
      toast.error("Hikaye en az 20 karakter olmalı");
      return;
    }
    setSavingLossEdit(true);
    try {
      const res = await axios.patch(
        `${API}/admin/loss-record/${selectedLossRecord.loss_id}`,
        editLossForm
      );
      const updated = res.data?.record;
      toast.success("Kayıt güncellendi. İlgili tüm göstergeler senkronize edildi.");
      if (updated) {
        setSelectedLossRecord(updated);
        setLossRecords((prev) =>
          prev.map((r) => (r.loss_id === updated.loss_id ? updated : r))
        );
      }
      fetchData();
      setIsEditingLoss(false);
      setEditLossForm(null);
    } catch (e) {
      const detail = e.response?.data?.detail || "Kayıt güncellenemedi";
      toast.error(detail);
    } finally {
      setSavingLossEdit(false);
    }
  };
  
  // Daily count form
  const today = new Date().toISOString().split('T')[0];
  const [countForm, setCountForm] = useState({
    count_date: today,
    total_dogs: 0,
    barinakta: 0,
    sahiplendirmeye_hazir: 0,
    koruyucu_ailede: 0,
    sahiplendirildi_today: 0,
    kayip_today: 0,
    vefat_today: 0,
    notes: ""
  });
  
  // Loss record form
  const [lossForm, setLossForm] = useState({
    dog_id: "",
    loss_type: "kayip",
    loss_reason: "bilinmiyor",
    loss_date: today,
    story: "",
    last_seen_location: "",
    last_seen_date: "",
    additional_notes: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dogsRes, lossRes, countsRes] = await Promise.all([
        axios.get(`${API}/dogs?limit=200`),
        axios.get(`${API}/transparency/loss-list`),
        axios.get(`${API}/admin/daily-counts?limit=30`)
      ]);
      setDogs(dogsRes.data.dogs.filter(d => d.status !== "kayip" && d.status !== "vefat"));
      setLossRecords(lossRes.data.loss_records || []);
      setDailyCounts(countsRes.data.counts || []);
      
      // Pre-fill count form with current stats
      const currentCounts = {
        barinakta: dogsRes.data.dogs.filter(d => d.status === "barinakta").length,
        sahiplendirmeye_hazir: dogsRes.data.dogs.filter(d => d.status === "sahiplendirmeye_hazir").length,
        koruyucu_ailede: dogsRes.data.dogs.filter(d => d.status === "koruyucu_ailede").length,
      };
      setCountForm(prev => ({
        ...prev,
        total_dogs: dogsRes.data.total,
        ...currentCounts
      }));
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  const openEditDailyCount = (count) => {
    setCountForm({
      count_date: count.count_date,
      total_dogs: count.total_dogs || 0,
      barinakta: count.barinakta || 0,
      sahiplendirmeye_hazir: count.sahiplendirmeye_hazir || 0,
      koruyucu_ailede: count.koruyucu_ailede || 0,
      sahiplendirildi_today: count.sahiplendirildi_today || 0,
      kayip_today: count.kayip_today || 0,
      vefat_today: count.vefat_today || 0,
      notes: count.notes || "",
    });
    setShowDailyCountDialog(true);
  };

  const handleDeleteDailyCount = async (countDate) => {
    if (!window.confirm(`${new Date(countDate).toLocaleDateString("tr-TR")} tarihli sayım kaydı silinsin mi?`)) return;
    try {
      await axios.delete(`${API}/admin/daily-counts/${countDate}`);
      toast.success("Sayım kaydı silindi");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Silinemedi");
    }
  };

  const handleSubmitDailyCount = async () => {
    try {
      await axios.post(`${API}/admin/daily-count`, countForm);
      toast.success("Günlük sayım kaydedildi");
      setShowDailyCountDialog(false);
      fetchData();
    } catch (error) {
      toast.error("Kayıt başarısız");
    }
  };

  const openLossDialog = (dog) => {
    setSelectedDog(dog);
    setLossForm({
      dog_id: dog.dog_id,
      loss_type: "kayip",
      loss_reason: "bilinmiyor",
      loss_date: today,
      story: "",
      last_seen_location: "",
      last_seen_date: "",
      additional_notes: ""
    });
    setShowLossDialog(true);
  };

  const handleSubmitLoss = async () => {
    if (!lossForm.story || lossForm.story.length < 20) {
      toast.error("Lütfen en az 20 karakterlik bir açıklama girin");
      return;
    }
    try {
      await axios.post(`${API}/admin/loss-record`, lossForm);
      toast.success(`${selectedDog?.name} için kayıp/vefat kaydı oluşturuldu`);
      setShowLossDialog(false);
      setSelectedDog(null);
      fetchData();
    } catch (error) {
      const d = error?.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map(e => e?.msg || String(e)).join(", ") : (d || "Kayıt başarısız");
      toast.error(msg);
    }
  };

  const getReasonLabel = (reason) => {
    const labels = {
      "hastalik": "Hastalık",
      "kaza": "Kaza",
      "yaslilik": "Yaşlılık",
      "bilinmiyor": "Bilinmiyor",
      "kayip_kacis": "Kaçış",
      "kayip_calinma": "Çalınma şüphesi",
      "kayip_diger": "Diğer"
    };
    return labels[reason] || reason;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl flex items-center gap-3">
            <Shield className="w-8 h-8 text-primary" />
            Şeffaflık Yönetimi
          </h1>
          <p className="text-muted-foreground">Yaşam Kanıtı - Günlük sayım ve kayıp/vefat kayıtları</p>
        </div>
        
        <div className="flex gap-2">
          <a href="/seffaflik" target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="btn-pill">
              <Eye className="w-4 h-4 mr-2" />
              Herkese Açık Sayfa
            </Button>
          </a>
          <Dialog open={showDailyCountDialog} onOpenChange={setShowDailyCountDialog}>
            <DialogTrigger asChild>
              <Button className="btn-pill bg-primary text-primary-foreground">
                <ClipboardList className="w-4 h-4 mr-2" />
                Günlük Sayım Gir
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl">Günlük Sayım</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label>Tarih</Label>
                  <Input
                    type="date"
                    value={countForm.count_date}
                    onChange={(e) => setCountForm({ ...countForm, count_date: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Toplam Can</Label>
                    <Input
                      type="number"
                      value={countForm.total_dogs}
                      onChange={(e) => setCountForm({ ...countForm, total_dogs: parseInt(e.target.value) || 0 })}
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label>Alanda</Label>
                    <Input
                      type="number"
                      value={countForm.barinakta}
                      onChange={(e) => setCountForm({ ...countForm, barinakta: parseInt(e.target.value) || 0 })}
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label>Sahiplenmeye Hazır</Label>
                    <Input
                      type="number"
                      value={countForm.sahiplendirmeye_hazir}
                      onChange={(e) => setCountForm({ ...countForm, sahiplendirmeye_hazir: parseInt(e.target.value) || 0 })}
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label>Geçici Bakımda</Label>
                    <Input
                      type="number"
                      value={countForm.koruyucu_ailede}
                      onChange={(e) => setCountForm({ ...countForm, koruyucu_ailede: parseInt(e.target.value) || 0 })}
                      className="mt-1.5"
                    />
                  </div>
                </div>

                <div className="p-4 bg-muted/30 rounded-xl">
                  <Label className="text-base font-medium mb-3 block">Bugünkü Değişimler</Label>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs">Sahiplendirildi</Label>
                      <Input
                        type="number"
                        value={countForm.sahiplendirildi_today}
                        onChange={(e) => setCountForm({ ...countForm, sahiplendirildi_today: parseInt(e.target.value) || 0 })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-amber-600">Kayıp</Label>
                      <Input
                        type="number"
                        value={countForm.kayip_today}
                        onChange={(e) => setCountForm({ ...countForm, kayip_today: parseInt(e.target.value) || 0 })}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500">Vefat</Label>
                      <Input
                        type="number"
                        value={countForm.vefat_today}
                        onChange={(e) => setCountForm({ ...countForm, vefat_today: parseInt(e.target.value) || 0 })}
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label>Notlar (opsiyonel)</Label>
                  <Textarea
                    value={countForm.notes}
                    onChange={(e) => setCountForm({ ...countForm, notes: e.target.value })}
                    className="mt-1.5"
                    placeholder="Günle ilgili önemli notlar..."
                    rows={2}
                  />
                </div>

                <Button onClick={handleSubmitDailyCount} className="w-full">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Sayımı Kaydet
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-card border border-border/50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Toplam Kayıp/Vefat</p>
                <p className="font-heading text-3xl mt-1">{lossRecords.length}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border border-border/50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Kayıp</p>
                <p className="font-heading text-3xl mt-1 text-amber-600">
                  {lossRecords.filter(r => r.loss_type === "kayip").length}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border border-border/50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Vefat</p>
                <p className="font-heading text-3xl mt-1 text-slate-500">
                  {lossRecords.filter(r => r.loss_type === "vefat").length}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                <Heart className="w-6 h-6 text-slate-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-card border border-border/50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Sayım Kaydı</p>
                <p className="font-heading text-3xl mt-1 text-primary">{dailyCounts.length}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <ClipboardList className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Two Column Layout */}
      <div className="grid lg:grid-cols-2 gap-8">
        {/* Mark Dog as Lost/Deceased */}
        <Card className="bg-card border border-border/50">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Kayıp/Vefat Kaydı Oluştur
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Bir canı kayıp veya vefat olarak işaretlemek için aşağıdaki listeden seçin.
              Her kayıt için açıklayıcı bir hikaye girmeniz gerekecektir.
            </p>
            
            <div className="space-y-2 max-h-80 overflow-y-auto pr-2">
              {dogs.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Tüm canlar güvende!
                </p>
              ) : (
                dogs.map((dog) => (
                  <div 
                    key={dog.dog_id}
                    className="flex items-center justify-between p-3 bg-muted/30 rounded-xl hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      {dog.photo_url ? (
                        <img src={dog.photo_url} alt={dog.name} className="w-10 h-10 rounded-lg object-cover" />
                      ) : (
                        <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                          <PawPrint className="w-5 h-5 text-muted-foreground" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium">{dog.name}</p>
                        <p className="text-xs text-muted-foreground">{dog.microchip_number || dog.dog_id}</p>
                      </div>
                    </div>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => openLossDialog(dog)}
                      className="text-amber-600 border-amber-200 hover:bg-amber-50"
                    >
                      <AlertTriangle className="w-4 h-4 mr-1" />
                      Kayıp/Vefat
                    </Button>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Recent Loss Records */}
        <Card className="bg-card border border-border/50">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <FileText className="w-5 h-5 text-primary" />
              Son Kayıp/Vefat Kayıtları
            </CardTitle>
          </CardHeader>
          <CardContent>
            {lossRecords.length === 0 ? (
              <div className="text-center py-8">
                <Shield className="w-12 h-12 text-primary/20 mx-auto mb-3" />
                <p className="text-muted-foreground">Henüz kayıp/vefat kaydı yok.</p>
                <p className="text-sm text-muted-foreground">Bu iyi bir haber!</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
                {lossRecords.slice(0, 10).map((record) => (
                  <button
                    key={record.loss_id}
                    onClick={() => setSelectedLossRecord(record)}
                    className="w-full text-left p-3 bg-muted/30 rounded-xl hover:bg-muted/60 hover:ring-2 hover:ring-primary/20 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-all duration-150 cursor-pointer group"
                    data-testid={`loss-record-item-${record.loss_id}`}
                    aria-label={`${record.dog_name} kaydının detayını aç`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium group-hover:text-primary transition-colors">{record.dog_name}</span>
                        {record.loss_type === "kayip" ? (
                          <Badge className="bg-amber-100 text-amber-700 text-xs">Kayıp</Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-600 text-xs">Vefat</Badge>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {new Date(record.loss_date).toLocaleDateString("tr-TR")}
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground line-clamp-2">{record.story}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Sebep: {getReasonLabel(record.loss_reason)}
                    </p>
                    <p className="text-[10px] text-muted-foreground/70 mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      Detayları görmek için tıklayın →
                    </p>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Loss Record Detail Dialog — item'a tıklayınca açılır */}
      <Dialog
        open={!!selectedLossRecord}
        onOpenChange={(v) => {
          if (!v) {
            setSelectedLossRecord(null);
            setIsEditingLoss(false);
            setEditLossForm(null);
          }
        }}
      >
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="loss-record-detail-dialog">
          {selectedLossRecord && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-3 mb-1 flex-wrap">
                  <DialogTitle className="text-2xl font-heading">
                    {selectedLossRecord.dog_name}
                  </DialogTitle>
                  {(isEditingLoss ? editLossForm.loss_type : selectedLossRecord.loss_type) === "kayip" ? (
                    <Badge className="bg-amber-100 text-amber-700">Kayıp</Badge>
                  ) : (
                    <Badge className="bg-slate-100 text-slate-600">Vefat</Badge>
                  )}
                  {!isEditingLoss && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={startEditLoss}
                      className="ml-auto text-primary hover:text-primary hover:bg-primary/10"
                      data-testid="loss-record-edit-btn"
                    >
                      <Edit className="w-3.5 h-3.5 mr-1" />
                      Düzenle
                    </Button>
                  )}
                </div>
                <DialogDescription>
                  {isEditingLoss ? (
                    <span className="text-xs">Aşağıdaki alanlarda değişiklik yapabilirsin. Kaydettiğinde platform genelinde ilgili tüm göstergeler güncellenir.</span>
                  ) : (
                    <>
                      {new Date(selectedLossRecord.loss_date).toLocaleDateString("tr-TR", {
                        day: "numeric", month: "long", year: "numeric",
                      })}
                      {" · "}
                      <span className="text-foreground/70">
                        Sebep: {getReasonLabel(selectedLossRecord.loss_reason)}
                      </span>
                    </>
                  )}
                </DialogDescription>
              </DialogHeader>

              {/* GÖRÜNTÜLEME MODU */}
              {!isEditingLoss && (
                <div className="space-y-4 mt-2">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 font-medium">
                      Hikaye
                    </p>
                    <p className="text-sm text-foreground/90 whitespace-pre-wrap leading-relaxed bg-muted/30 rounded-lg p-3 border border-border/50">
                      {selectedLossRecord.story || "—"}
                    </p>
                  </div>

                  {(selectedLossRecord.last_seen_location || selectedLossRecord.last_seen_date) && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {selectedLossRecord.last_seen_location && (
                        <div>
                          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1 font-medium">
                            Son Görülme Yeri
                          </p>
                          <p className="text-sm text-foreground/90">{selectedLossRecord.last_seen_location}</p>
                        </div>
                      )}
                      {selectedLossRecord.last_seen_date && (
                        <div>
                          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1 font-medium">
                            Son Görülme Tarihi
                          </p>
                          <p className="text-sm text-foreground/90">
                            {new Date(selectedLossRecord.last_seen_date).toLocaleDateString("tr-TR")}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {selectedLossRecord.additional_notes && (
                    <div>
                      <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 font-medium">
                        Ek Not
                      </p>
                      <p className="text-sm text-foreground/80 whitespace-pre-wrap leading-relaxed">
                        {selectedLossRecord.additional_notes}
                      </p>
                    </div>
                  )}

                  <div className="pt-3 border-t border-border/50 flex items-center justify-between gap-4 text-[11px] text-muted-foreground">
                    <div className="flex flex-col gap-0.5">
                      <span>Kayıt ID: <code className="font-mono text-[10px]">{selectedLossRecord.loss_id}</code></span>
                      {selectedLossRecord.created_at && (
                        <span>Kaydedildi: {new Date(selectedLossRecord.created_at).toLocaleString("tr-TR")}</span>
                      )}
                      {selectedLossRecord.updated_at && (
                        <span>Güncellendi: {new Date(selectedLossRecord.updated_at).toLocaleString("tr-TR")}</span>
                      )}
                    </div>
                    {selectedLossRecord.dog_id && (
                      <Link
                        to={`/can/${selectedLossRecord.dog_id}`}
                        className="text-primary hover:underline flex items-center gap-1 shrink-0"
                        data-testid="loss-record-view-dog-link"
                      >
                        Köpek profili →
                      </Link>
                    )}
                  </div>
                </div>
              )}

              {/* DÜZENLEME MODU */}
              {isEditingLoss && editLossForm && (
                <div className="space-y-4 mt-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 font-medium block">
                        Tür
                      </label>
                      <Select
                        value={editLossForm.loss_type}
                        onValueChange={(v) => setEditLossForm({ ...editLossForm, loss_type: v })}
                      >
                        <SelectTrigger data-testid="edit-loss-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="kayip">Kayıp</SelectItem>
                          <SelectItem value="vefat">Vefat</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 font-medium block">
                        Sebep
                      </label>
                      <Select
                        value={editLossForm.loss_reason}
                        onValueChange={(v) => setEditLossForm({ ...editLossForm, loss_reason: v })}
                      >
                        <SelectTrigger data-testid="edit-loss-reason">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {editLossForm.loss_type === "vefat" ? (
                            <>
                              <SelectItem value="hastalik">Hastalık</SelectItem>
                              <SelectItem value="kaza">Kaza</SelectItem>
                              <SelectItem value="yaslilik">Yaşlılık</SelectItem>
                              <SelectItem value="bilinmiyor">Bilinmiyor</SelectItem>
                            </>
                          ) : (
                            <>
                              <SelectItem value="kayip_kacis">Kaçış</SelectItem>
                              <SelectItem value="kayip_calinma">Çalınma şüphesi</SelectItem>
                              <SelectItem value="kayip_diger">Diğer</SelectItem>
                              <SelectItem value="bilinmiyor">Bilinmiyor</SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 font-medium block">
                      Olay Tarihi
                    </label>
                    <Input
                      type="date"
                      value={editLossForm.loss_date}
                      onChange={(e) => setEditLossForm({ ...editLossForm, loss_date: e.target.value })}
                      data-testid="edit-loss-date"
                    />
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 font-medium block">
                      Hikaye <span className="text-destructive normal-case tracking-normal font-normal">(en az 20 karakter)</span>
                    </label>
                    <Textarea
                      value={editLossForm.story}
                      onChange={(e) => setEditLossForm({ ...editLossForm, story: e.target.value })}
                      rows={4}
                      placeholder="Ne olduğunu açıklayın..."
                      data-testid="edit-loss-story"
                    />
                    <p className="text-[11px] text-muted-foreground mt-1">
                      {editLossForm.story.length} karakter
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 font-medium block">
                        Son Görülme Yeri <span className="text-muted-foreground/70 normal-case tracking-normal font-normal">(ops.)</span>
                      </label>
                      <Input
                        value={editLossForm.last_seen_location}
                        onChange={(e) => setEditLossForm({ ...editLossForm, last_seen_location: e.target.value })}
                        placeholder="Örn. Cadde, mahalle..."
                        data-testid="edit-loss-location"
                      />
                    </div>
                    <div>
                      <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 font-medium block">
                        Son Görülme Tarihi <span className="text-muted-foreground/70 normal-case tracking-normal font-normal">(ops.)</span>
                      </label>
                      <Input
                        type="date"
                        value={editLossForm.last_seen_date}
                        onChange={(e) => setEditLossForm({ ...editLossForm, last_seen_date: e.target.value })}
                        data-testid="edit-loss-last-seen-date"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5 font-medium block">
                      Ek Not <span className="text-muted-foreground/70 normal-case tracking-normal font-normal">(ops.)</span>
                    </label>
                    <Textarea
                      value={editLossForm.additional_notes}
                      onChange={(e) => setEditLossForm({ ...editLossForm, additional_notes: e.target.value })}
                      rows={2}
                      data-testid="edit-loss-notes"
                    />
                  </div>

                  <div className="pt-3 border-t border-border/50 flex items-center justify-end gap-2">
                    <Button
                      variant="ghost"
                      onClick={cancelEditLoss}
                      disabled={savingLossEdit}
                      data-testid="edit-loss-cancel"
                    >
                      İptal
                    </Button>
                    <Button
                      onClick={saveLossEdit}
                      disabled={savingLossEdit}
                      className="bg-primary text-primary-foreground hover:bg-primary/90"
                      data-testid="edit-loss-save"
                    >
                      {savingLossEdit ? "Kaydediliyor..." : "Kaydet"}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Daily Count History */}
      <Card className="bg-card border border-border/50">
        <CardHeader>
          <CardTitle className="font-heading text-xl flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" />
            Günlük Sayım Geçmişi
          </CardTitle>
        </CardHeader>
        <CardContent>
          {dailyCounts.length === 0 ? (
            <div className="text-center py-8">
              <ClipboardList className="w-12 h-12 text-primary/20 mx-auto mb-3" />
              <p className="text-muted-foreground">Henüz günlük sayım kaydı yok.</p>
              <p className="text-sm text-muted-foreground">İlk sayımı yukarıdaki butona tıklayarak girin.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border/50 text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="text-left p-3 font-medium">Tarih</th>
                    <th className="text-center p-3 font-medium">Toplam<br /><span className="normal-case font-normal">can</span></th>
                    <th className="text-center p-3 font-medium">Alanda</th>
                    <th className="text-center p-3 font-medium">Sahiplenmeye<br />Hazır</th>
                    <th className="text-center p-3 font-medium">Geçici<br />Bakımda</th>
                    <th className="text-center p-3 font-medium text-green-600">Sahiplendirildi<br /><span className="normal-case font-normal">(o gün)</span></th>
                    <th className="text-center p-3 font-medium text-amber-600">Kayıp<br /><span className="normal-case font-normal">(o gün)</span></th>
                    <th className="text-center p-3 font-medium text-slate-500">Vefat<br /><span className="normal-case font-normal">(o gün)</span></th>
                    <th className="text-left p-3 font-medium">Not</th>
                    <th className="text-right p-3 font-medium">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyCounts.map((count) => (
                    <tr key={count.count_id || count.count_date} className="border-b border-border/50 hover:bg-muted/20 align-top">
                      <td className="p-3 text-sm font-medium whitespace-nowrap">
                        {new Date(count.count_date).toLocaleDateString("tr-TR")}
                      </td>
                      <td className="p-3 text-center font-medium">{count.total_dogs}</td>
                      <td className="p-3 text-center">{count.barinakta}</td>
                      <td className="p-3 text-center">{count.sahiplendirmeye_hazir}</td>
                      <td className="p-3 text-center">{count.koruyucu_ailede}</td>
                      <td className="p-3 text-center text-green-600">{count.sahiplendirildi_today || 0}</td>
                      <td className="p-3 text-center text-amber-600">{count.kayip_today || 0}</td>
                      <td className="p-3 text-center text-slate-500">{count.vefat_today || 0}</td>
                      <td className="p-3 text-sm text-muted-foreground max-w-[220px] whitespace-pre-wrap break-words">{count.notes || "—"}</td>
                      <td className="p-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDailyCount(count)}
                            title="Sayımı düzenle"
                            data-testid={`daily-count-edit-${count.count_date}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-destructive"
                            onClick={() => handleDeleteDailyCount(count.count_date)}
                            title="Sayımı sil"
                            data-testid={`daily-count-delete-${count.count_date}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Loss Record Dialog */}
      <Dialog open={showLossDialog} onOpenChange={setShowLossDialog}>
        <DialogContent className="max-h-[90vh] overflow-y-auto max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-amber-500" />
              Kayıp/Vefat Kaydı
            </DialogTitle>
          </DialogHeader>
          
          {selectedDog && (
            <div className="space-y-4 mt-4">
              {/* Dog Info */}
              <div className="p-4 bg-muted/30 rounded-xl flex items-center gap-4">
                {selectedDog.photo_url ? (
                  <img src={selectedDog.photo_url} alt={selectedDog.name} className="w-16 h-16 rounded-xl object-cover" />
                ) : (
                  <div className="w-16 h-16 rounded-xl bg-muted flex items-center justify-center">
                    <PawPrint className="w-8 h-8 text-muted-foreground" />
                  </div>
                )}
                <div>
                  <p className="font-heading text-lg">{selectedDog.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {selectedDog.breed || "Karışık"} · {selectedDog.microchip_number || "Mikroçip yok"}
                  </p>
                </div>
              </div>

              {/* Loss Type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Kayıt Türü *</Label>
                  <Select value={lossForm.loss_type} onValueChange={(v) => setLossForm({ ...lossForm, loss_type: v })}>
                    <SelectTrigger className="mt-1.5">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="kayip">Kayıp</SelectItem>
                      <SelectItem value="vefat">Vefat</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Tarih *</Label>
                  <Input
                    type="date"
                    value={lossForm.loss_date}
                    onChange={(e) => setLossForm({ ...lossForm, loss_date: e.target.value })}
                    className="mt-1.5"
                  />
                </div>
              </div>

              {/* Reason */}
              <div>
                <Label>Sebep *</Label>
                <Select value={lossForm.loss_reason} onValueChange={(v) => setLossForm({ ...lossForm, loss_reason: v })}>
                  <SelectTrigger className="mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {lossForm.loss_type === "kayip" ? (
                      <>
                        <SelectItem value="kayip_kacis">Kaçış</SelectItem>
                        <SelectItem value="kayip_calinma">Çalınma şüphesi</SelectItem>
                        <SelectItem value="kayip_diger">Diğer</SelectItem>
                        <SelectItem value="bilinmiyor">Bilinmiyor</SelectItem>
                      </>
                    ) : (
                      <>
                        <SelectItem value="hastalik">Hastalık</SelectItem>
                        <SelectItem value="kaza">Kaza</SelectItem>
                        <SelectItem value="yaslilik">Yaşlılık</SelectItem>
                        <SelectItem value="bilinmiyor">Bilinmiyor</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Story - Required */}
              <div>
                <Label>Açıklama / Hikaye * (en az 20 karakter)</Label>
                <Textarea
                  value={lossForm.story}
                  onChange={(e) => setLossForm({ ...lossForm, story: e.target.value })}
                  className="mt-1.5"
                  placeholder={lossForm.loss_type === "kayip" 
                    ? "Ne oldu? Ne zaman fark edildi? Arama çalışmaları yapıldı mı?..." 
                    : "Sağlık durumu nasıldı? Ne oldu? Detaylar..."}
                  rows={4}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {lossForm.story.length}/20 karakter (minimum)
                </p>
              </div>

              {/* Location for Lost */}
              {lossForm.loss_type === "kayip" && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Son Görüldüğü Yer</Label>
                    <Input
                      value={lossForm.last_seen_location}
                      onChange={(e) => setLossForm({ ...lossForm, last_seen_location: e.target.value })}
                      className="mt-1.5"
                      placeholder="Örn: Pativista bahçesi"
                    />
                  </div>
                  <div>
                    <Label>Son Görülme Tarihi</Label>
                    <Input
                      type="date"
                      value={lossForm.last_seen_date}
                      onChange={(e) => setLossForm({ ...lossForm, last_seen_date: e.target.value })}
                      className="mt-1.5"
                    />
                  </div>
                </div>
              )}

              {/* Additional Notes */}
              <div>
                <Label>Ek Notlar (opsiyonel)</Label>
                <Textarea
                  value={lossForm.additional_notes}
                  onChange={(e) => setLossForm({ ...lossForm, additional_notes: e.target.value })}
                  className="mt-1.5"
                  placeholder="Varsa ek bilgiler..."
                  rows={2}
                />
              </div>

              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowLossDialog(false)} className="flex-1">
                  İptal
                </Button>
                <Button 
                  onClick={handleSubmitLoss} 
                  className={`flex-1 ${lossForm.loss_type === "kayip" ? "bg-amber-600 hover:bg-amber-700" : "bg-slate-600 hover:bg-slate-700"}`}
                  disabled={lossForm.story.length < 20}
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  {lossForm.loss_type === "kayip" ? "Kayıp Olarak İşaretle" : "Vefat Olarak İşaretle"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ============================================================================
// Pilot Dashboard - Real-time pilot transparency metrics for shelter admins
// ============================================================================
const PILOT_METRIC_GROUPS = [
  {
    key: "A",
    title: "Veri Kalitesi",
    color: "from-sky-500/15 to-sky-500/0",
    accent: "text-sky-600",
    metricKeys: ["a1_profile_completeness", "a3_profile_freshness"],
  },
  {
    key: "B",
    title: "Süreç Hızı",
    color: "from-emerald-500/15 to-emerald-500/0",
    accent: "text-emerald-600",
    metricKeys: ["b1_first_response_time", "b2_application_completion"],
  },
  {
    key: "C",
    title: "Sahiplendirme Sonucu",
    color: "from-orange-500/15 to-orange-500/0",
    accent: "text-orange-600",
    metricKeys: ["c1_time_to_home", "c3_ninety_day_success"],
  },
  {
    key: "D",
    title: "Finansal Sürdürülebilirlik",
    color: "from-violet-500/15 to-violet-500/0",
    accent: "text-violet-600",
    metricKeys: ["d1_active_regular_support", "d3_care_cost_coverage"],
  },
  {
    key: "E",
    title: "Yaşam Kanıtı",
    color: "from-rose-500/15 to-rose-500/0",
    accent: "text-rose-600",
    metricKeys: ["e1_life_proof_compliance"],
  },
];

const STATUS_META = {
  excellent: { label: "Mükemmel", classes: "bg-emerald-100 text-emerald-800 border-emerald-200", dot: "bg-emerald-500" },
  good: { label: "İyi", classes: "bg-sky-100 text-sky-800 border-sky-200", dot: "bg-sky-500" },
  fair: { label: "Orta", classes: "bg-amber-100 text-amber-800 border-amber-200", dot: "bg-amber-500" },
  needs_attention: { label: "Dikkat", classes: "bg-rose-100 text-rose-800 border-rose-200", dot: "bg-rose-500" },
  no_data: { label: "Veri Yok", classes: "bg-slate-100 text-slate-700 border-slate-200", dot: "bg-slate-400" },
  no_data_yet: { label: "Veri Bekleniyor", classes: "bg-slate-100 text-slate-700 border-slate-200", dot: "bg-slate-400" },
};

function formatDateTR(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("tr-TR", {
      day: "2-digit", month: "long", year: "numeric"
    });
  } catch { return iso; }
}

function formatMetricValue(metric) {
  if (!metric) return "—";
  const { status, numerator, denominator, unit } = metric;
  if (status === "no_data" || status === "no_data_yet") return "—";
  const n = (typeof numerator === "number") ? numerator : 0;
  if (unit === "%") return `%${n}`;
  if (denominator && denominator !== 100) return `${n} / ${denominator}`;
  return `${n}${unit ? " " + unit : ""}`;
}

function MetricCard({ metric, idx }) {
  if (!metric) return null;
  const status = metric.status || "no_data";
  const meta = STATUS_META[status] || STATUS_META.no_data;
  const isNoData = status === "no_data" || status === "no_data_yet";
  const percentage = (metric.denominator && metric.denominator !== 0 && typeof metric.numerator === "number")
    ? Math.min(100, Math.round((metric.numerator / metric.denominator) * 100))
    : null;

  return (
    <Card
      className="bg-card border border-border/50 hover:border-primary/30 transition-colors h-full"
      data-testid={`pilot-metric-card-${metric.key || idx}`}
    >
      <CardContent className="p-5 flex flex-col h-full">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide" data-testid={`metric-label-${metric.key}`}>
              {metric.label}
            </p>
            <p className="font-heading text-3xl mt-1.5 leading-tight" data-testid={`metric-value-${metric.key}`}>
              {isNoData ? <span className="text-muted-foreground/50">—</span> : formatMetricValue(metric)}
            </p>
          </div>
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border ${meta.classes} whitespace-nowrap`}>
            <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`}></span>
            {meta.label}
          </span>
        </div>

        {percentage !== null && !isNoData && (
          <div className="mt-1 mb-3">
            <div className="h-1.5 bg-muted rounded-full overflow-hidden">
              <div
                className={`h-full ${meta.dot} transition-all`}
                style={{ width: `${percentage}%` }}
              ></div>
            </div>
          </div>
        )}

        {metric.monthly_total_try !== undefined && metric.monthly_total_try !== null && (
          <p className="text-sm text-foreground mb-2">
            <span className="text-muted-foreground">Aylık taahhüt:</span>{" "}
            <span className="font-semibold tabular-nums">{metric.monthly_total_try.toLocaleString("tr-TR")}₺</span>
          </p>
        )}

        <p className="text-xs text-muted-foreground leading-relaxed flex-1">
          {metric.description || ""}
        </p>

        <div className="mt-3 pt-3 border-t border-border/50 flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
          <span data-testid={`metric-sample-${metric.key}`}>
            Örneklem: <span className="font-medium text-foreground">{metric.sample_size ?? 0}</span>
          </span>
          {metric.public_safe ? (
            <span className="inline-flex items-center gap-1 text-emerald-700">
              <Eye className="w-3 h-3" /> Halka açık
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-amber-700">
              <Shield className="w-3 h-3" /> Yalnız admin
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function PilotDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [starting, setStarting] = useState(false);
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [error, setError] = useState(null);
  const [lastFetchedAt, setLastFetchedAt] = useState(null);
  const [platformPreflight, setPlatformPreflight] = useState(null);

  const fetchMetrics = async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/pilot/metrics`);
      setData(res.data);
      setLastFetchedAt(new Date());
    } catch (e) {
      console.error("Pilot metrics fetch error:", e);
      const unauth = e.response?.status === 401;
      setError(unauth
        ? "Oturumun düşmüş görünüyor. Panele tekrar giriş yapıp bu sayfayı yenile."
        : (e.response?.data?.detail || "Metrikler yüklenemedi."));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchPreflight = async () => {
    try {
      const res = await axios.get(`${API}/stats`);
      setPlatformPreflight(res.data);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { fetchMetrics(); fetchPreflight(); }, []);

  // Auto-refresh every 60 seconds so the dashboard reflects the real state without manual reload
  useEffect(() => {
    const interval = setInterval(() => { fetchMetrics(true); }, 60000);
    return () => clearInterval(interval);
  }, []);

  const isConfirmed = confirmText.trim().toLocaleUpperCase("tr-TR") === "PILOTU BAŞLAT";

  const handleStartPilot = async () => {
    if (!isConfirmed) {
      toast.error('Onaylamak için "PILOTU BAŞLAT" yazmanız gerekiyor.');
      return;
    }
    setStarting(true);
    try {
      const res = await axios.post(`${API}/admin/pilot/start`);
      toast.success(res.data?.message || "Pilot başlatıldı");
      setShowStartDialog(false);
      setConfirmText("");
      await fetchMetrics();
    } catch (e) {
      console.error("Pilot start error:", e);
      toast.error(e.response?.data?.detail || "Pilot başlatılamadı");
    } finally {
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="pilot-loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <h1 className="font-heading text-3xl md:text-4xl">Pilot Raporu</h1>
        <Card className="border-rose-200 bg-rose-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-rose-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-rose-900">Veri yüklenemedi</p>
                <p className="text-sm text-rose-700 mt-1">{error}</p>
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => fetchMetrics()}
                  data-testid="pilot-retry-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-2" /> Tekrar Dene
                </Button>
                {error?.includes("Oturum") && (
                  <Button
                    size="sm"
                    className="mt-3 ml-2"
                    onClick={() => { window.location.href = "/admin"; }}
                    data-testid="pilot-relogin-btn"
                  >
                    Tekrar Giriş Yap
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Not started state
  if (!data?.started) {
    return (
      <div className="space-y-6 max-w-3xl" data-testid="pilot-not-started">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl mb-2">Pilot Raporu</h1>
          <p className="text-muted-foreground">Pativista pilot programı için gerçek zamanlı şeffaflık metrikleri</p>
        </div>

        {/* Pre-flight platform snapshot — helps admin decide WHEN to start */}
        {platformPreflight && (
          <Card className="bg-card border-border/50" data-testid="pilot-preflight-card">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-3">
                <ClipboardList className="w-4 h-4 text-muted-foreground" />
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Pilotu başlatmadan önce platformdaki güncel durum
                </p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Toplam Can</p>
                  <p className="font-heading text-2xl mt-0.5 tabular-nums" data-testid="preflight-dogs">
                    {platformPreflight.total_dogs ?? 0}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Sahiplendirilen</p>
                  <p className="font-heading text-2xl mt-0.5 tabular-nums">
                    {platformPreflight.adopted_dogs ?? 0}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Aktif Koruyucu</p>
                  <p className="font-heading text-2xl mt-0.5 tabular-nums">
                    {platformPreflight.active_guardians ?? 0}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Bekleyen Başvuru</p>
                  <p className="font-heading text-2xl mt-0.5 tabular-nums">
                    {platformPreflight.pending_applications ?? 0}
                  </p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-3 leading-relaxed">
                Pilot başlatıldığında bu kayıtlar baseline öncesi olarak kalır; metrikler yalnızca
                başlangıç tarihinden <strong>sonra</strong> eklenen veriler üzerinden hesaplanır.
                Yeterli can bilgisini yüklemeden başlatmak istersiniz; aksi halde ilk aylarda
                metrikler düşük örneklem ile çıkar.
              </p>
            </CardContent>
          </Card>
        )}

        <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50">
          <CardContent className="p-7">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Activity className="w-6 h-6 text-amber-700" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <h2 className="font-heading text-2xl mb-2">Pilot henüz başlatılmamış</h2>
                <p className="text-sm text-foreground/80 leading-relaxed mb-4">
                  Pilot başlatıldığında bu tarihten sonra eklenen <strong>köpekler, başvurular,
                  koruyucu aileler ve takip e-postaları</strong> dahil edilerek 9 temel metrik
                  hesaplanmaya başlanır. Bu baseline tarihi <strong>geri alınamaz</strong> ve
                  hem panelinizdeki bu raporda hem de kamuya açık şeffaflık sayfasında
                  referans olarak kullanılır.
                </p>
                <div className="bg-white/70 rounded-xl p-4 mb-4 border border-amber-200/50">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                    Başlattığınızda ölçülmeye başlayacak metrikler
                  </p>
                  <div className="grid sm:grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                    {PILOT_METRIC_GROUPS.map(g => (
                      <div key={g.key} className="flex items-center gap-2">
                        <span className={`w-1.5 h-1.5 rounded-full ${g.dot || "bg-foreground/40"}`}></span>
                        <span><strong>{g.key}.</strong> {g.title}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <Button
                  onClick={() => setShowStartDialog(true)}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="pilot-start-btn"
                >
                  <Play className="w-4 h-4 mr-2" /> Pilotu Başlat
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Dialog open={showStartDialog} onOpenChange={(o) => { setShowStartDialog(o); if (!o) setConfirmText(""); }}>
          <DialogContent data-testid="pilot-start-confirm-dialog">
            <DialogHeader>
              <DialogTitle>Pilot Programını Başlat</DialogTitle>
              <DialogDescription>
                Pilot baseline tarihini şu ana sabitler. Bu işlem geri alınamaz.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                <p className="font-medium text-amber-900 mb-1">Bu işlem geri alınamaz</p>
                <p className="text-amber-800 text-xs leading-relaxed">
                  Şu andan itibaren eklenen tüm veriler pilot kapsamına dahil olur ve
                  şeffaflık raporlamasında baseline tarihi olarak kullanılır.
                </p>
              </div>
              <div>
                <Label htmlFor="confirm-text" className="text-sm">
                  Onaylamak için aşağıdaki kutuya <strong>PILOTU BAŞLAT</strong> yazın:
                </Label>
                <Input
                  id="confirm-text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="PILOTU BAŞLAT"
                  className="mt-2"
                  data-testid="pilot-start-confirm-input"
                  autoComplete="off"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowStartDialog(false)} className="flex-1">
                  İptal
                </Button>
                <Button
                  onClick={handleStartPilot}
                  disabled={starting || !isConfirmed}
                  className="flex-1 bg-amber-600 hover:bg-amber-700"
                  data-testid="pilot-start-confirm-btn"
                >
                  {starting ? "Başlatılıyor..." : "Pilotu Başlat"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Active pilot state
  const metrics = data.metrics || {};
  const totalDays = (data.duration_months || 6) * 30;
  const progress = Math.min(100, Math.round(((data.days_elapsed || 0) / totalDays) * 100));

  return (
    <div className="space-y-6" data-testid="pilot-active-dashboard">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <h1 className="font-heading text-3xl md:text-4xl">Pilot Raporu</h1>
            <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1.5"></span>
              Aktif
            </Badge>
          </div>
          <p className="text-muted-foreground text-sm">
            Yalnızca <strong>{formatDateTR(data.go_live_at)}</strong> sonrasına ait gerçek veriler gösterilir
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastFetchedAt && (
            <span className="text-xs text-muted-foreground hidden sm:inline" data-testid="pilot-last-updated">
              Son güncelleme: {lastFetchedAt.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchMetrics(true)}
            disabled={refreshing}
            data-testid="pilot-refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Yenile
          </Button>
        </div>
      </div>

      {/* Timeline overview */}
      <Card className="bg-gradient-to-br from-primary/5 to-accent/5 border-border/50" data-testid="pilot-timeline-card">
        <CardContent className="p-6">
          <div className="grid sm:grid-cols-4 gap-4 mb-4">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Başlangıç</p>
              <p className="font-heading text-lg mt-1" data-testid="pilot-start-date">{formatDateTR(data.go_live_at)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Süre</p>
              <p className="font-heading text-lg mt-1">{data.duration_months || 6} ay</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Geçen Süre</p>
              <p className="font-heading text-lg mt-1" data-testid="pilot-days-elapsed">{data.days_elapsed || 0} gün</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide">Kalan</p>
              <p className="font-heading text-lg mt-1">{data.days_remaining ?? 0} gün</p>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-muted-foreground">Pilot ilerlemesi</span>
              <span className="text-xs font-medium tabular-nums">{progress}%</span>
            </div>
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all"
                style={{ width: `${progress}%` }}
                data-testid="pilot-progress-bar"
              ></div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Metric Groups */}
      {PILOT_METRIC_GROUPS.map((group) => {
        const groupMetrics = group.metricKeys.map(k => metrics[k]).filter(Boolean);
        if (groupMetrics.length === 0) return null;
        return (
          <div key={group.key} data-testid={`pilot-group-${group.key}`}>
            <div className="flex items-baseline gap-3 mb-3">
              <span className={`font-heading text-2xl ${group.accent}`}>{group.key}.</span>
              <h2 className="font-heading text-xl">{group.title}</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {groupMetrics.map((m, i) => (
                <MetricCard key={m.key || i} metric={m} idx={i} />
              ))}
            </div>
          </div>
        );
      })}

      {/* Footer note */}
      <Card className="bg-muted/30 border-border/50">
        <CardContent className="p-4">
          <p className="text-xs text-muted-foreground leading-relaxed">
            <strong className="text-foreground">Veri kaynağı:</strong> Tüm metrikler canlı veritabanından,
            pilot başlangıç tarihinden ({formatDateTR(data.go_live_at)}) sonra oluşturulmuş kayıtlar üzerinden
            hesaplanır. <strong className="text-foreground">&ldquo;Halka açık&rdquo;</strong> işaretli metrikler kamuya
            açık şeffaflık sayfasında da gösterilir; <strong className="text-foreground">&ldquo;Yalnız admin&rdquo;</strong>{" "}
            işaretli metrikler operasyonel KPI olup yalnızca bu panelde görünür. Veri olmayan metriklerde
            değer yerine <span className="font-mono">—</span> gösterilir; uydurma/varsayılan değer
            <strong> kullanılmaz</strong>.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
